import { useState, useEffect, useRef } from 'react'
import ListingCard from '../ListingCard/ListingCard'
import ChatThread from '../ChatThread/ChatThread'
import AdoptionFormPage, { generateAdoptionPDF } from '../AdoptionFormPage/AdoptionFormPage'
import { ordersAPI, invoicesAPI, messagesAPI, favoritesAPI, authAPI, listingsAPI, adoptionAPI, trackingAPI, deliveryAPI, animalDocsAPI } from '../../api'
import { compressImages } from '../ChatThread/imageCompression'
import TransportView from './TransportView'
import './UserDashboard.css'


const CATEGORIES = ['Chiens','Chats','Oiseaux','Reptiles','Lapins','Rongeurs','Poissons','Chevaux']

const BREEDS_BY_CAT = {
  'Chiens':   ['Berger Allemand','Golden Retriever','Labrador','Husky de Sibérie','Border Collie','Bouledogue Français','Spitz Nain','Cavalier King Charles','Berger Australien','Yorkshire Terrier','Beagle','Chihuahua','Rottweiler','Malinois','Caniche'],
  'Chats':    ['Maine Coon','Bengal','Ragdoll','British Shorthair','Persan','Sphynx','Scottish Fold','Birman','Sibérien','Norvégien des Forêts','Abyssin','Chartreux'],
  'Oiseaux':  ['Gris du Gabon','Conure Soleil','Calopsitte','Amazone','Cacatoès','Perruche Ondulée','Inséparable'],
  'Reptiles': ['Dragon Barbu','Gecko Léopard','Python Royal','Boa Constrictor','Iguane Vert','Tortue Grecque'],
  'Lapins':   ['Bélier Nain','Angora','Rex','Nain de Hollande','Lionhead'],
  'Rongeurs': ["Cochon d'Inde",'Hamster Syrien','Chinchilla','Rat Domestique','Gerbille'],
  'Poissons': ['Carpe Koï','Guppy','Betta','Discus','Scalaire','Poisson Rouge'],
  'Chevaux':  ['Poney Shetland','Âne','Quarter Horse','Pur-sang','Andalou'],
}

const REGIONS = ['Île-de-France','Auvergne-Rhône-Alpes','Bretagne','Nouvelle-Aquitaine','Occitanie','PACA','Hauts-de-France','Grand Est','Normandie','Pays de la Loire','Centre-Val de Loire']

const DELIVERY_OPTIONS = ['Livraison incluse','Livraison non incluse']

function BreedSelect({ category, options, value, onChange, placeholder }) {
  const [search, setSearch] = useState('')
  const [open, setOpen]     = useState(false)
  const ref      = useRef(null)
  const inputRef = useRef(null)
  const allOptions = options || BREEDS_BY_CAT[category] || []
  const filtered   = allOptions.filter(o => o.toLowerCase().includes(search.toLowerCase()))
  const ph = placeholder || (category ? 'Sélectionner une race…' : 'Sélectionner…')
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  const handleOpen    = () => { setOpen(true); setSearch(''); setTimeout(() => inputRef.current?.focus(), 30) }
  const handleSelect  = (val) => { onChange(val); setOpen(false); setSearch('') }
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && search.trim()) handleSelect(search.trim())
    if (e.key === 'Escape') setOpen(false)
  }
  return (
    <div className="breed-wrap" ref={ref}>
      <div className="ud-input breed-display" onClick={handleOpen} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', userSelect:'none' }}>
        <span style={{ color: value ? 'var(--blue)' : 'var(--slate-lt)', fontSize:14 }}>{value || ph}</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform .2s', color:'var(--slate-lt)', flexShrink:0 }}><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {open && (
        <div className="breed-dropdown">
          <div className="breed-search">
            <input ref={inputRef} placeholder="Rechercher ou taper…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKeyDown} onClick={e => e.stopPropagation()} />
          </div>
          <div className="breed-list">
            {search.trim() && !allOptions.some(o => o.toLowerCase() === search.toLowerCase()) && (
              <div className="breed-item breed-item-custom" onClick={() => handleSelect(search.trim())}>
                <span style={{ fontSize:11, color:'var(--slate)', marginRight:5 }}>Utiliser :</span>
                <strong>{search.trim()}</strong>
              </div>
            )}
            {filtered.length === 0 && !search && <div className="breed-empty">Commencez à taper…</div>}
            {filtered.length === 0 && search && <div className="breed-empty">Appuyez sur Entrée pour utiliser votre saisie</div>}
            {filtered.map(o => (
              <div key={o} className={`breed-item${value === o ? ' selected' : ''}`} onClick={() => handleSelect(o)}>{o}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const fmt = (n) => n.toLocaleString('fr-FR') + ' €'

// Affiche le mode de paiement : "Visa ••••4242" ou "PayPal" etc.
const fmtPay = (method, brand, last4) => {
  if (method === 'card' && brand && last4) {
    const b = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
    return `${b} ••••${last4}`
  }
  const labels = { card:'Carte bancaire', paypal:'PayPal', link:'Link', apple_pay:'Apple Pay', google_pay:'Google Pay', free:'Gratuit' }
  return labels[method] || 'Carte bancaire'
}

/* ── Icônes SVG ── */
const IcoDashboard = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
const IcoOrders   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
const IcoHeart    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
const IcoMessages = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
const IcoInvoice  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
const IcoUser     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
const IcoSettings = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
const IcoLogout   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
const IcoEye      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
const IcoTrash    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
const IcoEdit     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
const IcoSave     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
const IcoDownload = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
const IcoCheck    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcoTruck    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
const IcoShield   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
const IcoBell     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
const IcoLock     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>

const IcoWait     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
const IcoListings = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
const IcoPlus     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const IcoUpload   = () => <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
const IcoX        = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
const IcoBan      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>

const PAGE_TITLES = {
  overview: 'Accueil',
  orders:   'Mes achats',
  favorites:'Mes favoris',
  messages: 'Mes messages',
  listings: 'Mes annonces',
  invoices: 'Mes documents',
  transport:'Mes transports',
  profile:  'Mon profil',
  settings: 'Paramètres',
}

// Cache jsPDF pour éviter de recharger le module à chaque PDF
let _cachedJsPDF = null
const _getJsPDFUser = async () => {
  if (_cachedJsPDF) return _cachedJsPDF
  const mod = await import('jspdf')
  _cachedJsPDF = mod.jsPDF
  return _cachedJsPDF
}

// Cache logo pour éviter un fetch réseau à chaque génération PDF
let _cachedLogoUser = null
const _getLogoUser = async () => {
  if (_cachedLogoUser) return _cachedLogoUser
  try {
    const r = await fetch(require('../../assets/monlogo.jpeg'))
    const b = await r.blob()
    _cachedLogoUser = await new Promise(res => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(b) })
  } catch (e) {}
  return _cachedLogoUser
}

export default function UserDashboard({
  user            = { name:'', email:'', loggedIn:false },
  wishlist        = [],
  onLogout        = () => {},
  onFav           = () => {},
  onDetailClick   = () => {},
  onCategoryClick = () => {},
  onBack          = () => {},
  onUpdateUser    = () => {},
  onAdminFormalites = () => {},
  initialView     = 'overview',  // ← permet d'ouvrir directement un onglet (ex: 'messages')
  contactListing  = null,        // ← annonce concernée si on arrive via "Contacter l'annonceur"
}) {
  const [activeView, setActiveView]       = useState(initialView)
  const [menuOpen, setMenuOpen]           = useState(false)
  const [ordersPage, setOrdersPage]       = useState(1)
  const [favsPage, setFavsPage]           = useState(1)
  const [invoicesPage, setInvoicesPage]   = useState(1)
  const [invoiceTab, setInvoiceTab]       = useState('invoices') // 'invoices' | 'adoption' | 'a-signer'
  const [pendingForms,          setPendingForms]          = useState([])
  const [pendingInvoices,       setPendingInvoices]       = useState([])
  const [pendingLoading,        setPendingLoading]        = useState(false)
  const [pendingDocsBadge,      setPendingDocsBadge]      = useState(0)
  const [pendingFilter,         setPendingFilter]         = useState('forms')
  const [viewingDoc,            setViewingDoc]            = useState(null)
  const [signingDoc,            setSigningDoc]            = useState(null)
  const [signSaving,            setSignSaving]            = useState(false)
  const [downloadingDeliveryId, setDownloadingDeliveryId] = useState(null)
  const [animalDocs,       setAnimalDocs]       = useState([])
  const [animalDocBadge,   setAnimalDocBadge]   = useState(0)
  const [animalDocsLoading,setAnimalDocsLoading]= useState(false)
  const [viewingAnimalDoc, setViewingAnimalDoc] = useState(null)
  const [signingAnimalDoc, setSigningAnimalDoc] = useState(null)
  const [animalDocFilter,  setAnimalDocFilter]  = useState('all')
  const [animalDocPage,    setAnimalDocPage]    = useState(1)
  const ANIM_DOC_PER = 8
  const [animalSignSaving, setAnimalSignSaving] = useState(false)
  const [dlAnimalDocId,    setDlAnimalDocId]    = useState(null)
  const sigCanvasRef  = useRef(null)
  const sigDrawingRef = useRef(false)
  const sigLastPos    = useRef({ x:0, y:0 })
  const [adoptionForms, setAdoptionForms] = useState([])
  const [adoptionFormsBadge, setAdoptionFormsBadge] = useState(0)
  const [adoptionLoading, setAdoptionLoading] = useState(false)
  const [downloadingAdoptionId, setDownloadingAdoptionId] = useState(null)
  const [showAdoptionForm, setShowAdoptionForm] = useState(false)
  const [myTrips, setMyTrips]                 = useState([])
  const [tripsLoading, setTripsLoading]       = useState(false)
  const [tripsNewBadge, setTripsNewBadge]     = useState(0)  // trajets non vus depuis dernière visite
  const [ordersBadge, setOrdersBadge]     = useState(0)
  const [invoicesBadge, setInvoicesBadge] = useState(0)
  const [loading, setLoading]             = useState(true)

  const knownInvoicesUnread = useRef(0)

  // ── Données depuis l'API ──
  const [orders, setOrders]               = useState([])
  const [userInvoices, setUserInvoices]   = useState([])
  const [conversations, setConversations]     = useState([])   // une par annonce contactée (+ "general")
  const [selectedConvKey, setSelectedConvKey] = useState(null) // ObjectId de l'annonce, ou 'general'
  const [threadMsgs, setThreadMsgs]           = useState([])
  const [threadLoading, setThreadLoading]     = useState(false)
  const [adminStatus, setAdminStatus]         = useState(null) // { online, lastActive } — statut de l'administrateur
  const [favListings, setFavListings]     = useState([])

  // ── Mes annonces (publication par l'utilisateur, soumise à validation admin) ──
  const [myListings, setMyListings]         = useState([])
  const [listingsLoading, setListingsLoading] = useState(false)
  const [showListingForm, setShowListingForm] = useState(false)
  const [editingListing, setEditingListing] = useState(null)   // annonce en cours d'édition, ou null = nouvelle
  const [listingSaving, setListingSaving]   = useState(false)
  const [confirmDelListing, setConfirmDelListing] = useState(null) // { id, title }
  const [listingMsg, setListingMsg]         = useState('')

  // ── Badge « Mes annonces » : notifie l'utilisateur quand l'admin a traité
  // une annonce (approuvée ou rejetée) depuis sa dernière visite de l'onglet.
  // On garde en localStorage le dernier statut connu de chaque annonce ; toute
  // différence détectée par le polling incrémente le badge, et la visite de
  // l'onglet remet tout à zéro (façon badge de messages non lus).
  const [listingNotifBadge, setListingNotifBadge] = useState(0)
  const LISTING_STATUS_SEEN_KEY = 'user_listing_status_seen'
  const activeViewRef = useRef(initialView)
  useEffect(() => { activeViewRef.current = activeView }, [activeView])

  // ── Refs pour stopper le polling quand l'utilisateur remplit un formulaire ──
  const adoptionFormOpenRef = useRef(false)
  const signingDocOpenRef   = useRef(false)
  useEffect(() => { adoptionFormOpenRef.current = showAdoptionForm }, [showAdoptionForm])
  useEffect(() => { signingDocOpenRef.current   = !!signingAnimalDoc }, [signingAnimalDoc])

  // Champs du formulaire de publication
  const [lTitle, setLTitle]         = useState('')
  const [lCat, setLCat]             = useState('Chiens')
  const [lBreed, setLBreed]         = useState('')
  const [lAge, setLAge]             = useState('')
  const [lSex, setLSex]             = useState('Mâle')
  const [lPrice, setLPrice]         = useState('')
  const [lCity, setLCity]           = useState('')
  const [lRegion, setLRegion]       = useState('')
  const [lDelivery, setLDelivery]   = useState('Livraison incluse')
  const [lSeller, setLSeller]       = useState('')
  const [lSellerType, setLSellerType] = useState('Part')
  const [lPhone, setLPhone]         = useState('')
  const [lDesc, setLDesc]           = useState('')
  const [lVacc, setLVacc]           = useState(false)
  const [lMicro, setLMicro]         = useState(false)
  const [lPedi, setLPedi]           = useState(false)
  const [lImgs, setLImgs]           = useState([])   // [{ file, url }]
  const [lImgError, setLImgError]   = useState('')
  const [lCompressing, setLCompressing] = useState(false)
  const [lDrag, setLDrag]               = useState(false)
  const lFileRef = useRef(null)

  const [editing, setEditing]             = useState(false)
  const [saved, setSaved]                 = useState(false)
  const [chatSending, setChatSending]     = useState(false)
  const [notifEmail, setNotifEmail]       = useState(true)
  const [notifMsg, setNotifMsg]           = useState(true)
  const [notifShip, setNotifShip]         = useState(false)

  // User fields
  const [fName, setFName]       = useState(user.name    || '')
  const [fEmail, setFEmail]     = useState(user.email   || '')
  const [fPhone, setFPhone]     = useState(user.phone   || '')
  const [fAddress, setFAddress] = useState(user.address || '')
  const [fCity, setFCity]       = useState(user.city    || '')

  // ── Charger toutes les données ──
  const loadAll = async () => {
    setLoading(true)
    try {
      const [ordersData, invoicesData, favsData] = await Promise.all([
        ordersAPI.getMy({ limit: 50 }),
        invoicesAPI.getMy({ limit: 50 }),
        favoritesAPI.getMy({ limit: 50 }),
      ])

      const newOrdersList = (ordersData.orders || []).map(o => ({
        ...o,
        orderId:        o._id,
        orderNumber:    o.orderNumber || `CMD-${new Date().getFullYear()}-${o._id.toString().slice(-6)}`,
        orderDate:      new Date(o.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }),
        title:          o.listingSnapshot?.title    || '',
        breed:          o.listingSnapshot?.breed    || '',
        category:       o.listingSnapshot?.category || '',
        city:           o.listingSnapshot?.city     || '',
        price:          o.amount,
        img:            o.listingSnapshot?.image    || '',
        paymentMethod:  o.paymentMethod             || 'wire',
        paymentStatus:  o.paymentStatus             || 'pending_wire',
        wireReference:  o.wireReference             || '',
        deliveryStatus: o.deliveryStatus            || 'pending',
        isNew:          !o.readByBuyer,
      }))

      const newInvoicesList = (invoicesData.invoices || []).map(inv => ({
        ...inv,
        id:            inv._id,
        ref:           inv.invoiceNumber || `FACT-${new Date().getFullYear()}-${inv._id.toString().slice(-6)}`,
        animal:        inv.listingInfo?.title    || '',
        category:      inv.listingInfo?.category || '',
        amount:        inv.amount,
        date:          new Date(inv.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }),
        isNew:         !inv.readByBuyer,
        paymentMethod: inv.paymentMethod || 'card',
        status:        inv.status || 'paid',
      }))

      // ── Badges ──
      const unseenOrders = newOrdersList.filter(o => o.isNew).length
      setOrdersBadge(unseenOrders)

      const newUnread = newInvoicesList.filter(i => i.isNew).length
      setInvoicesBadge(newUnread)
      knownInvoicesUnread.current = newUnread

      setOrders(newOrdersList)
      setUserInvoices(newInvoicesList)

      setFavListings((favsData.favorites || []).filter(f => f.listing).map(f => ({
        ...f.listing,
        id:  f.listing._id,
        img: f.listing.images?.[0]?.url || '',
      })))
    } catch (err) {
      console.error('Erreur dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, []) // eslint-disable-line
  useEffect(() => { loadPendingDocs(initialView === 'invoices') }, []) // eslint-disable-line

  // Charge la liste des conversations dès le montage — nécessaire pour que le
  // badge "Mes messages" du menu soit correct même avant de visiter l'onglet
  useEffect(() => { loadConversations() }, []) // eslint-disable-line

  // Charge mes annonces dès le montage — nécessaire pour le badge "Mes annonces"
  useEffect(() => { loadMyListings() }, []) // eslint-disable-line
  useEffect(() => { loadMyTrips(initialView === 'transport') }, []) // eslint-disable-line

  // ── Si on arrive directement avec une annonce précise (bouton "Contacter
  // l'annonceur"), on ouvre immédiatement la conversation correspondante ──
  useEffect(() => {
    if (initialView === 'messages' && contactListing) {
      const key = contactListing.id || contactListing._id
      if (key) openConversation(key)
    }
  }, []) // eslint-disable-line

  // ── BroadcastChannel : rafraîchit instantanément thread + docs quand l'admin envoie ──
  useEffect(() => {
    let ch
    try {
      ch = new BroadcastChannel('app_sync_event')
      ch.onmessage = async () => {
        refreshThread()
        try {
          const docsData = await animalDocsAPI.getMyDocs()
          const docs = docsData.docs || []
          setAnimalDocs(docs)
          const unread = docs.filter(d => !d.readByUser).length
          if (activeViewRef.current !== 'invoices') setAnimalDocBadge(unread)
        } catch {}
        try {
          const data = await adoptionAPI.getMy()
          setAdoptionForms(data.forms || [])
        } catch {}
        try {
          const pending = await deliveryAPI.getMyPendingDocs()
          setPendingForms(pending.forms || [])
          setPendingInvoices(pending.invoices || [])
          if (activeViewRef.current !== 'invoices') {
            const u = [...(pending.forms || []), ...(pending.invoices || [])].filter(d => !d.readByUser && d.signatureStatus === 'awaiting_user').length
            setPendingDocsBadge(u)
          }
        } catch {}
      }
    } catch {}
    return () => { try { ch && ch.close() } catch {} }
  }, []) // eslint-disable-line

  // ── SSE : met à jour le statut de livraison des commandes en temps réel ──
  useEffect(() => {
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000'
    let es
    try {
      es = new EventSource(`${BASE_URL}/events`)
      es.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data)
          if (msg.type === 'order_update' && msg.orderId) {
            setOrders(prev => prev.map(o =>
              (o.orderId === msg.orderId || o._id === msg.orderId)
                ? { ...o, deliveryStatus: msg.deliveryStatus }
                : o
            ))
          }
        } catch (e2) {}
      }
      es.onerror = () => { try { es.close() } catch (e3) {} }
    } catch (e4) {}
    return () => { try { es && es.close() } catch (e5) {} }
  }, []) // eslint-disable-line

  // ── Polling léger : rafraîchit la liste des conversations pendant que
  // l'onglet est actif (nouvelles conversations, aperçus, badges) ──
  useEffect(() => {
    if (activeView !== 'messages') return
    const interval = setInterval(loadConversations, 10000)
    return () => clearInterval(interval)
  }, [activeView]) // eslint-disable-line

  // ── Polling léger : rafraîchit le fil ouvert pendant que l'onglet est actif ──
  useEffect(() => {
    if (activeView !== 'messages' || !selectedConvKey) return
    const interval = setInterval(refreshThread, 2000)
    return () => clearInterval(interval)
  }, [activeView, selectedConvKey]) // eslint-disable-line

  // ── Polling léger : statut en ligne/hors ligne de l'administrateur ──
  useEffect(() => {
    if (activeView !== 'messages') return
    const pollStatus = async () => {
      try {
        const data = await messagesAPI.getAdminStatus()
        setAdminStatus({ online: data.online, lastActive: data.lastActive })
      } catch {}
    }
    pollStatus()
    const interval = setInterval(pollStatus, 15000)
    return () => clearInterval(interval)
  }, [activeView])

  // ── Polling global 15s — toutes les sections ──
  // Chaque section se rafraîchit silencieusement toutes les 15s,
  // ce qui remplace les boutons "Actualiser" manuels.
  useEffect(() => {
    const poll = async () => {
      try {
        // ── Commandes ──
        const ordersData = await ordersAPI.getMy({ limit: 50 })
        const refreshedOrders = (ordersData.orders || []).map(o => ({
          ...o,
          orderId:        o._id,
          orderNumber:    o.orderNumber || `CMD-${new Date().getFullYear()}-${o._id.toString().slice(-6)}`,
          orderDate:      new Date(o.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }),
          title:          o.listingSnapshot?.title    || '',
          breed:          o.listingSnapshot?.breed    || '',
          category:       o.listingSnapshot?.category || '',
          city:           o.listingSnapshot?.city     || '',
          price:          o.amount,
          img:            o.listingSnapshot?.image    || '',
          paymentMethod:  o.paymentMethod             || 'wire',
          paymentStatus:  o.paymentStatus             || 'pending_wire',
          wireReference:  o.wireReference             || '',
          deliveryStatus: o.deliveryStatus            || 'pending',
          isNew:          !o.readByBuyer,
        }))
        setOrders(prev => {
          const hasChange = refreshedOrders.some(r => {
            const p = prev.find(x => x.orderId === r.orderId)
            return !p || p.deliveryStatus !== r.deliveryStatus || p.isNew !== r.isNew
          }) || refreshedOrders.length !== prev.length
          return hasChange ? refreshedOrders : prev
        })
        setOrdersBadge(refreshedOrders.filter(o => o.isNew).length)
      } catch { /* silencieux */ }

      try {
        // ── Factures ──
        const invoicesData = await invoicesAPI.getMy({ limit: 50 })
        const refreshedInv = (invoicesData.invoices || []).map(inv => ({
          ...inv,
          id:            inv._id,
          ref:           inv.invoiceNumber || `FACT-${new Date().getFullYear()}-${inv._id.toString().slice(-6)}`,
          animal:        inv.listingInfo?.title    || '',
          category:      inv.listingInfo?.category || '',
          amount:        inv.amount,
          date:          new Date(inv.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }),
          isNew:         !inv.readByBuyer,
          paymentMethod: inv.paymentMethod || 'card',
          status:        inv.status || 'paid',
        }))
        setUserInvoices(prev => {
          const hasChange = refreshedInv.length !== prev.length ||
            refreshedInv.some(r => { const p = prev.find(x => x.id === r.id); return !p || p.isNew !== r.isNew })
          return hasChange ? refreshedInv : prev
        })
        setInvoicesBadge(refreshedInv.filter(i => i.isNew).length)
      } catch { /* silencieux */ }

      try {
        // ── Favoris ──
        const favsData = await favoritesAPI.getMy({ limit: 50 })
        const refreshedFavs = (favsData.favorites || []).filter(f => f.listing).map(f => ({
          ...f.listing,
          id:  f.listing._id,
          img: f.listing.images?.[0]?.url || '',
        }))
        setFavListings(prev => {
          const hasChange = refreshedFavs.length !== prev.length ||
            refreshedFavs.some(r => !prev.find(x => x.id === r.id))
          return hasChange ? refreshedFavs : prev
        })
      } catch { /* silencieux */ }

      try {
        // ── Mes annonces ──
        loadMyListings(activeViewRef.current === 'listings')
      } catch { /* silencieux */ }

      try {
        // ── Transports ──
        const d = await trackingAPI.getMy()
        const tripsObj = d.trips || {}
        const progObj  = d.tripsProgress || {}
        const list = Object.values(tripsObj).map(trip => ({
          ...trip,
          progress: progObj[trip.id] || progObj[trip.trackingId] || null,
        }))
        setMyTrips(prev => {
          const hasChange = list.length !== prev.length ||
            list.some(r => { const p = prev.find(x => (x.id || x.trackingId) === (r.id || r.trackingId)); return !p })
          return hasChange ? list : prev
        })
        // Badge transports
        let seen = {}
        try { seen = JSON.parse(localStorage.getItem('user_trips_seen') || '{}') } catch {}
        const newOnes = list.filter(t => !seen[t.id || t.trackingId])
        if (activeViewRef.current !== 'transport') setTripsNewBadge(newOnes.length)
      } catch { /* silencieux */ }

      // ── Ne pas rafraîchir les docs si un formulaire est en cours de remplissage ──
      // Évite de perturber l'état interne des formulaires (signature, adoption…).
      if (!adoptionFormOpenRef.current && !signingDocOpenRef.current) {
        try {
          // ── Formulaires d'adoption (si onglet documents actif) ──
          if (activeViewRef.current === 'invoices') {
            const data = await adoptionAPI.getMy()
            setAdoptionForms(data.forms || [])
          }
        } catch { /* silencieux */ }

        try {
          const docsData = await animalDocsAPI.getMyDocs()
          const docs = docsData.docs || []
          setAnimalDocs(prev => {
            const hasChange = docs.length !== prev.length ||
              docs.some(d => { const p = prev.find(x => x._id === d._id); return !p || p.signatureStatus !== d.signatureStatus || p.readByUser !== d.readByUser })
            return hasChange ? docs : prev
          })
          const unread = docs.filter(d => !d.readByUser).length
          if (activeViewRef.current !== 'invoices') setAnimalDocBadge(unread)
        } catch { /* silencieux */ }

        try {
          const data = await adoptionAPI.getMy()
          setAdoptionForms(data.forms || [])
        } catch { /* silencieux */ }
      }
    }

    const interval = setInterval(poll, 15000)
    return () => clearInterval(interval)
  }, []) // eslint-disable-line

  const handleNavClick = (id) => {
    setActiveView(id)
    if (id === 'orders')   { setOrdersBadge(0);   loadAll() }
    if (id === 'invoices') { setInvoicesBadge(0); setPendingDocsBadge(0); setAnimalDocBadge(0); setAdoptionFormsBadge(0); loadAll(); loadAdoptionForms(true); loadPendingDocs(true) }
    if (id === 'messages') { loadConversations() }
    if (id === 'favorites') loadAll()
    if (id === 'listings') { loadMyListings(true) }
    if (id === 'transport') loadMyTrips(true)  // acknowledge=true → badge remis à zéro
  }

  const unreadMsgs  = conversations.reduce((s, c) => s + (c.unreadCount || 0), 0)
  const newOrders   = orders.filter(o => o.isNew).length
  const newInvoices = userInvoices.filter(i => i.isNew).length
  const initials    = (user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2)

  const handleSave = async () => {
    try {
      await authAPI.updateMe({ name:fName, phone:fPhone, address:fAddress, city:fCity })
      onUpdateUser({ name:fName, email:fEmail, phone:fPhone, address:fAddress, city:fCity })
      setEditing(false); setSaved(true); setTimeout(() => setSaved(false), 3000)
    } catch (err) { console.error(err) }
  }

  // ── Charge la liste des conversations (une par annonce contactée) ──
  const loadConversations = async () => {
    try {
      const data = await messagesAPI.getMyConversations()
      setConversations(data.conversations || [])
    } catch {}
  }

  // ── Ouvre une conversation : charge son thread et la marque comme lue ──
  const openConversation = async (key) => {
    setSelectedConvKey(key)
    setThreadLoading(true)
    try {
      const data = await messagesAPI.getMyConversationThread(key)
      setThreadMsgs(data.messages || [])
      setConversations(cs => cs.map(c => c.key === key ? { ...c, unreadCount: 0 } : c))
    } catch {} finally {
      setThreadLoading(false)
    }
  }

  // ── Rafraîchit le thread actuellement ouvert (polling) ──
  const refreshThread = async () => {
    if (!selectedConvKey) return
    try {
      const data = await messagesAPI.getMyConversationThread(selectedConvKey)
      setThreadMsgs(data.messages || [])
      setConversations(cs => cs.map(c => c.key === selectedConvKey ? { ...c, unreadCount: 0 } : c))
    } catch {}
  }

  // ── Envoie un message à l'admin dans la conversation ouverte (texte et/ou plusieurs images) ──
  const sendChatMessage = async (text, imageFiles) => {
    setChatSending(true)
    try {
      const listingId = selectedConvKey && selectedConvKey !== 'general' ? selectedConvKey : null
      const res = await messagesAPI.send({
        subject:   'Conversation',
        body:      text,
        listingId,
        imageFiles,
      })
      setThreadMsgs(m => [...m, res.message])
      await loadConversations() // rafraîchit la liste — nouvelle conversation ou aperçu mis à jour
      // Notifie l'admin en temps réel (autre onglet / autre fenêtre)
      try { new BroadcastChannel('app_sync_event').postMessage({ type: 'new_message' }) } catch {}
    } catch (err) {
      console.error('Erreur envoi message:', err)
    } finally {
      setChatSending(false)
    }
  }

  // ── Supprime un message — 'pour moi' (disparaît de ma vue) ou 'pour tout le
  // monde' (remplacé par un placeholder des deux côtés, réservé à l'expéditeur) ──
  const handleDeleteMessage = async (messageId, forEveryone) => {
    try {
      await messagesAPI.delete(messageId, forEveryone)
      if (forEveryone) {
        setThreadMsgs(m => m.map(x => x._id === messageId ? { ...x, deletedForEveryone: true, body: '', images: [] } : x))
      } else {
        setThreadMsgs(m => m.filter(x => x._id !== messageId))
      }
      await loadConversations() // l'aperçu du dernier message peut avoir changé
    } catch (err) {
      console.error('Erreur suppression message:', err)
    }
  }

  // ── Supprime la conversation actuellement ouverte (pour moi uniquement) ──
  const handleDeleteConversation = async () => {
    if (!selectedConvKey) return
    try {
      await messagesAPI.deleteMyConversation(selectedConvKey)
      setSelectedConvKey(null)
      setThreadMsgs([])
      await loadConversations()
    } catch (err) {
      console.error('Erreur suppression conversation:', err)
    }
  }

  // ══════════════════════════════════════════════════
  // MES ANNONCES — publication soumise à validation admin
  // ══════════════════════════════════════════════════

  // ── Charge mes annonces (tous statuts confondus) ──
  // acknowledge=true : l'utilisateur consulte activement l'onglet « Mes annonces »
  // → on marque tous les statuts actuels comme vus et le badge repasse à zéro.
  // acknowledge=false (polling en arrière-plan) : on compare avec les statuts
  // déjà vus pour détecter une décision de l'admin (pending → active/rejected)
  // et incrémenter le badge en conséquence, sans effacer la référence.
  const loadMyListings = async (acknowledge = false) => {
    setListingsLoading(true)
    try {
      const data = await listingsAPI.getMy({ limit: 50 })
      const fetched = data.listings || []

      let seenMap = {}
      try { seenMap = JSON.parse(localStorage.getItem(LISTING_STATUS_SEEN_KEY) || '{}') } catch {}

      if (acknowledge) {
        const updated = {}
        fetched.forEach(l => { updated[l._id] = l.status })
        localStorage.setItem(LISTING_STATUS_SEEN_KEY, JSON.stringify(updated))
        setListingNotifBadge(0)
      } else {
        const changed = fetched.filter(l => {
          const prevStatus = seenMap[l._id]
          return prevStatus !== undefined && prevStatus !== l.status && (l.status === 'active' || l.status === 'rejected')
        })
        setListingNotifBadge(changed.length)

        // Nouvelles annonces jamais vues : on les enregistre avec leur statut
        // actuel comme référence de base, sans déclencher de notification
        const merged = { ...seenMap }
        let mergedChanged = false
        fetched.forEach(l => { if (!(l._id in merged)) { merged[l._id] = l.status; mergedChanged = true } })
        if (mergedChanged) localStorage.setItem(LISTING_STATUS_SEEN_KEY, JSON.stringify(merged))
      }

      setMyListings(fetched)
    } catch {} finally {
      setListingsLoading(false)
    }
  }

  const resetListingForm = () => {
    setLTitle(''); setLCat('Chiens'); setLBreed(''); setLAge(''); setLSex('Mâle')
    setLPrice(''); setLCity(fCity || ''); setLRegion(''); setLDelivery('Livraison incluse')
    setLSeller(fName || ''); setLSellerType('Part'); setLPhone(fPhone || '')
    setLDesc(''); setLVacc(false); setLMicro(false); setLPedi(false)
    setLImgs(p => { p.forEach(f => URL.revokeObjectURL(f.url)); return [] })
    setLImgError(''); setEditingListing(null)
  }

  const openNewListingForm = () => {
    resetListingForm()
    setShowListingForm(true)
  }

  const openEditListingForm = (item) => {
    setEditingListing(item)
    setLTitle(item.title); setLCat(item.category); setLBreed(item.breed)
    setLAge(item.age); setLSex(item.sex); setLPrice(item.price)
    setLCity(item.city); setLRegion(item.region || '')
    setLDelivery(item.delivery || 'Livraison incluse')
    setLSeller(item.seller || fName || ''); setLSellerType(item.sellerType === 'Eleveur' ? 'Pro' : item.sellerType === 'Don' ? 'Don' : 'Part')
    setLPhone(item.phone || fPhone || '')
    setLDesc(item.desc || '')
    setLVacc(!!item.vaccinated); setLMicro(!!item.microchipped); setLPedi(!!item.pedigree)
    setLImgs([]); setLImgError('')
    setShowListingForm(true)
  }

  // ── Sélection et compression des photos avant envoi (max 10, comme côté admin) ──
  const handleListingFiles = async (files) => {
    const selected = Array.from(files || [])
    if (selected.length === 0) return
    const remainingSlots = 10 - lImgs.length
    let ignored = 0
    const toProcess = []
    for (const file of selected) {
      if (toProcess.length >= remainingSlots) { ignored++; continue }
      if (!file.type.startsWith('image/')) { ignored++; continue }
      if (file.size > 30 * 1024 * 1024) { ignored++; continue }
      toProcess.push(file)
    }
    setLImgError('')
    if (toProcess.length > 0) {
      setLCompressing(true)
      try {
        const compressed = await compressImages(toProcess, { maxDimension: 2000, targetMaxMB: 5 })
        const entries = compressed.map(f => ({ file: f, url: URL.createObjectURL(f) }))
        setLImgs(p => [...p, ...entries].slice(0, 10))
      } catch {
        setLImgError('Erreur lors de la préparation des photos. Réessayez.')
      } finally {
        setLCompressing(false)
      }
    }
    if (ignored > 0) setLImgError(`${ignored} photo(s) ignorée(s) — maximum 10 photos (JPG, PNG, WebP).`)
  }

  const removeListingImageAt = (idx) => {
    setLImgs(prev => {
      const target = prev[idx]
      if (target) URL.revokeObjectURL(target.url)
      return prev.filter((_, i) => i !== idx)
    })
  }

  // ── Publie ou met à jour l'annonce ──
  const saveListingForm = async () => {
    if (!lTitle.trim() || !lBreed.trim() || (lSellerType !== 'Don' && !lPrice) || !lCity.trim()) {
      setListingMsg('Titre, race, prix et ville sont requis'); return
    }
    if (lDesc.trim().length < 10) {
      setListingMsg('La description doit contenir au moins 10 caractères'); return
    }
    setListingMsg('')
    setListingSaving(true)
    try {
      const formData = new FormData()
      formData.append('title',        lTitle.trim())
      formData.append('breed',        lBreed.trim())
      formData.append('category',     lCat)
      formData.append('price',        lPrice)
      formData.append('age',          lAge.trim())
      formData.append('sex',          lSex)
      formData.append('city',         lCity.trim())
      formData.append('region',       lRegion)
      formData.append('seller',       lSeller.trim())
      formData.append('sellerType',   lSellerType)
      formData.append('phone',        lPhone.trim())
      formData.append('desc',         lDesc.trim())
      formData.append('vaccinated',   lVacc)
      formData.append('microchipped', lMicro)
      formData.append('pedigree',     lPedi)
      formData.append('delivery',     lDelivery)
      lImgs.forEach(entry => { if (entry.file) formData.append('images', entry.file) })

      if (editingListing) {
        const result = await listingsAPI.update(editingListing._id, formData)
        const updated = result.listing
        setMyListings(ls => ls.map(l =>
          l._id === updated._id
            ? { ...updated, img: updated.images?.[0]?.url || '', imgs: (updated.images || []).map(i => i.url) }
            : l
        ))
      } else {
        const result = await listingsAPI.create(formData)
        const created = result.listing
        setMyListings(ls => [
          { ...created, img: created.images?.[0]?.url || '', imgs: (created.images || []).map(i => i.url) },
          ...ls,
        ])
      }
      setShowListingForm(false)
      resetListingForm()
    } catch (err) {
      setListingMsg(err.message || 'Erreur lors de la publication')
    } finally {
      setListingSaving(false)
    }
  }

  // ── Supprime une de mes annonces ──
  const handleDeleteListing = async () => {
    if (!confirmDelListing) return
    const { id } = confirmDelListing
    setConfirmDelListing(null)
    try {
      await listingsAPI.delete(id)
      setMyListings(ls => ls.filter(l => l._id !== id))
    } catch (err) {
      setListingMsg(err.message || 'Erreur lors de la suppression')
    }
  }

  const handleOrderRead = async (orderId) => {
    try {
      await ordersAPI.markAsRead(orderId)
    } catch {} // silencieux : l'UI se met à jour même si l'API échoue
    setOrders(o => o.map(x => x.orderId===orderId ? {...x, isNew:false} : x))
    setOrdersBadge(b => Math.max(0, b - 1))
  }

  const markAllOrdersRead = async () => {
    try {
      await ordersAPI.markAllAsRead()
    } catch {}
    setOrders(o => o.map(x => ({...x, isNew:false})))
    setOrdersBadge(0)
  }

  const handleInvoiceRead  = async (id) => {
    try {
      if (id === 'all') { await invoicesAPI.markAllAsRead(); setUserInvoices(inv => inv.map(x => ({...x, isNew:false}))) }
      else { await invoicesAPI.markAsRead(id); setUserInvoices(inv => inv.map(x => x.id===id ? {...x, isNew:false} : x)) }
    } catch {}
  }

  const handleToggleFav    = async (listingId) => {
    try { await favoritesAPI.toggle(listingId); setFavListings(f => f.filter(l => l.id !== listingId)); onFav(listingId) } catch {}
  }

  const [downloadingInvId, setDownloadingInvId] = useState(null)

  const handleDownloadPDF = async (inv) => {
    setDownloadingInvId(inv.id)
    handleInvoiceRead(inv.id)
    try {
      // Charger le logo exactement comme le formulaire d'adoption
      const logoDataUrl = await _getLogoUser()

      // Récupérer les infos acheteur depuis le user connecté
      const buyerInfo = {
        name:    user?.name    || '',
        email:   user?.email   || '',
        phone:   user?.phone   || '',
        address: user?.address || '',
        city:    user?.city    || '',
      }

      const jsPDF = await _getJsPDFUser()
      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
      const W = 210, H = 297, M = 14
      const N=[18,34,74], G=[217,174,92], SL=[100,115,140], WH=[255,255,255], GN=[34,139,68]
      const PAY_LABELS = { card: 'Carte bancaire', paypal: 'PayPal', link: 'Link', apple_pay: 'Apple Pay', google_pay: 'Google Pay' }
      const dateStr = inv.date || new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
      const fa = (n) => Number(n||0).toLocaleString('fr-FR',{minimumFractionDigits:0,maximumFractionDigits:0}).replace(/\s/g,'')+' \u20AC'

      const fc = (...c) => doc.setFillColor(...c)
      const dc = (...c) => doc.setDrawColor(...c)
      const tc = (...c) => doc.setTextColor(...c)
      const lw = (w) => doc.setLineWidth(w)
      const txt = (s,x,y,opts) => doc.text(String(s||'—'),x,y,opts||{})

      // ── HEADER navy ──
      const hH=48
      fc(...N);doc.rect(0,0,W,hH,'F')
      fc(...G);doc.rect(0,0,4,hH,'F')
      fc(...WH);doc.roundedRect(M,6,30,30,4,4,'F')
      if(logoDataUrl){try{doc.addImage(logoDataUrl,'JPEG',M+1,7,28,28)}catch(e){}}
      tc(...WH);doc.setFontSize(16);doc.setFont('helvetica','bold')
      txt('Animaux pour Adoption',M+36,18)
      tc(...G);doc.setFontSize(10);doc.setFont('helvetica','normal')
      txt('de la France',M+36,27)
      tc(160,175,200);doc.setFontSize(7.5);doc.setFont('helvetica','normal')
      txt('animauxdecompagnie@animauxpouradoptiondelafrance.com',M+36,36)
      tc(...G);doc.setFontSize(30);doc.setFont('helvetica','bold')
      txt('FACTURE',W-M,20,{align:'right'})
      tc(160,175,200);doc.setFontSize(8);doc.setFont('helvetica','normal')
      txt(inv.ref||'',W-M,30,{align:'right'})

      let y=hH+8

      // ── ÉMIS PAR / FACTURÉ À — texte libre sans bordure ──
      const bw=(W-M*2-8)/2, b2x=M+bw+8, bh=38
      tc(...SL);doc.setFontSize(6.5);doc.setFont('helvetica','bold');txt('ÉMIS PAR',M,y+7)
      tc(...N);doc.setFontSize(9.5);doc.setFont('helvetica','bold');txt('Animaux pour Adoption de la France',M,y+15)
      tc(...SL);doc.setFontSize(7.5);doc.setFont('helvetica','normal')
      txt('animauxdecompagnie@animauxpouradoptiondelafrance.com',M,y+22)
      txt('+33 07 57 75 00 92',M,y+28)
      txt('Paris, France',M,y+34)

      tc(...SL);doc.setFontSize(6.5);doc.setFont('helvetica','bold');txt('FACTURÉ À',b2x,y+7)
      tc(...N);doc.setFontSize(9.5);doc.setFont('helvetica','bold');txt((buyerInfo.name||'—').slice(0,32),b2x,y+15)
      tc(...SL);doc.setFontSize(7.5);doc.setFont('helvetica','normal')
      let cy2=y+22
      if(buyerInfo.email){txt(buyerInfo.email.slice(0,36),b2x,cy2);cy2+=6}
      if(buyerInfo.phone){txt(buyerInfo.phone.slice(0,36),b2x,cy2);cy2+=6}
      const addrLine=[buyerInfo.address,buyerInfo.city].filter(Boolean).join(', ')
      if(addrLine)txt(addrLine.slice(0,36),b2x,cy2)

      y+=bh+8

      // ── BARRE MÉTADONNÉES navy arrondie ──
      const mH=22
      fc(...N);doc.roundedRect(M,y,W-M*2,mH,3,3,'F')
      const mW=(W-M*2)/4
      ;[
        ["DATE D'ÉMISSION",  dateStr],
        ['DATE DE PAIEMENT', dateStr],
        ['MÉTHODE',          PAY_LABELS[inv.paymentMethod]||'Carte bancaire'],
        ['STATUT',           null],
      ].forEach(([label,val],i)=>{
        const mx=M+i*mW+5
        tc(...G);doc.setFontSize(5.5);doc.setFont('helvetica','bold');txt(label,mx,y+7)
        if(val===null){
          fc(...GN);doc.roundedRect(mx,y+9,20,8,2,2,'F')
          tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
          doc.text('PAYÉ',mx+10,y+14.5,{align:'center'})
        } else {
          tc(...WH);doc.setFontSize(8);doc.setFont('helvetica','normal');txt(String(val).slice(0,22),mx,y+16)
        }
      })
      y+=mH+8

      // ── TABLEAU ──
      const TW=W-M*2
      const xArt=M+4, xCat=M+68, xQte=M+98, xPu=M+114, xTot=M+168

      fc(...N);doc.rect(M,y,TW,9,'F')
      tc(...G);doc.setFontSize(5.5);doc.setFont('helvetica','bold')
      doc.text('DESCRIPTION', xArt,y+6)
      doc.text('CATÉGORIE',   xCat,y+6)
      doc.text('QTÉ',         xQte,y+6)
      doc.text('PRIX UNIT.',  xPu, y+6)
      doc.text('TOTAL',       xTot,y+6,{align:'right'})
      y+=9

      fc(252,252,255);doc.rect(M,y,TW,14,'F')
      tc(...N);doc.setFontSize(8);doc.setFont('helvetica','bold')
      doc.text(String(inv.animal||'Animal'),  xArt,y+9.5)
      doc.setFont('helvetica','normal')
      doc.text(String(inv.category||'—'),     xCat,y+9.5)
      doc.text('1',                            xQte,y+9.5)
      doc.text(fa(inv.amount),                 xPu, y+9.5)
      tc(...G);doc.setFont('helvetica','bold')
      doc.text(fa(inv.amount),                 xTot,y+9.5,{align:'right'})
      y+=14

      dc(...N);lw(0.6);doc.line(M,y,M+TW,y)
      y+=10

      // ── NOTE + TOTAUX côte à côte ──
      const noteW=TW*0.46
      const totBlockX=M+noteW+10
      const totBlockW=TW-noteW-10
      const blockH=34

      fc(253,249,240);doc.roundedRect(M,y,noteW,blockH,2,2,'F')
      fc(...G);doc.rect(M,y,3,blockH,'F')
      tc(...N);doc.setFontSize(8.5);doc.setFont('helvetica','bold');txt('Note',M+7,y+10)
      tc(...SL);doc.setFontSize(7);doc.setFont('helvetica','normal')
      txt('Merci pour votre confiance.',M+7,y+18)
      txt("Ce document fait office de preuve d'achat officielle.",M+7,y+25)

      tc(...SL);doc.setFontSize(7.5);doc.setFont('helvetica','normal')
      fc(...N);doc.roundedRect(totBlockX,y+17,totBlockW,15,2,2,'F')
      tc(...G);doc.setFontSize(8.5);doc.setFont('helvetica','bold')
      txt('TOTAL PAYE',totBlockX+5,y+26.5)
      txt(fa(inv.amount),M+168,y+26.5,{align:'right'})

      y+=blockH+16

      // ── FOOTER ──
      const footY=H-22
      fc(...G);doc.rect(0,footY-1,W,1,'F')
      fc(...N);doc.rect(0,footY,W,22,'F')
      tc(...WH);doc.setFontSize(8);doc.setFont('helvetica','bold')
      txt('Animaux pour Adoption de la France  |  animauxdecompagnie@animauxpouradoptiondelafrance.com  |  +33 07 57 75 00 92',W/2,footY+9,{align:'center'})
      tc(160,175,200);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
      txt('Conservez ce justificatif.',W/2,footY+16,{align:'center'})

      doc.save(`${inv.ref || 'facture'}.pdf`)
    } catch (err) {
      alert('Erreur lors de la génération du PDF. Réessayez.')
    } finally {
      setDownloadingInvId(null)
    }
  }

  const TRIPS_SEEN_KEY = 'user_trips_seen'
  const loadMyTrips = async (acknowledge = false) => {
    setTripsLoading(true)
    try {
      const d = await trackingAPI.getMy()
      const tripsObj = d.trips         || {}
      const progObj  = d.tripsProgress || {}
      const list = Object.values(tripsObj).map(trip => ({
        ...trip,
        progress: progObj[trip.id] || progObj[trip.trackingId] || null,
      }))
      setMyTrips(list)
      // Badge : comparer les IDs connus avec ceux du localStorage
      let seen = {}
      try { seen = JSON.parse(localStorage.getItem(TRIPS_SEEN_KEY) || '{}') } catch {}
      if (acknowledge) {
        // L'user visite l'onglet — marquer tout comme vu, badge = 0
        const updated = {}
        list.forEach(t => { updated[t.id || t.trackingId] = true })
        localStorage.setItem(TRIPS_SEEN_KEY, JSON.stringify(updated))
        setTripsNewBadge(0)
      } else {
        // Polling ou chargement initial — compter les nouveaux non vus
        const newOnes = list.filter(t => !seen[t.id || t.trackingId])
        setTripsNewBadge(newOnes.length)
        // Enregistrer les nouveaux comme "connus" (pas encore "vus")
        const merged = { ...seen }
        let changed = false
        list.forEach(t => { const k = t.id || t.trackingId; if (!(k in merged)) { merged[k] = false; changed = true } })
        if (changed) localStorage.setItem(TRIPS_SEEN_KEY, JSON.stringify(merged))
      }
    } catch (e) { console.error('Erreur trajets:', e) }
    finally { setTripsLoading(false) }
  }

  const loadPendingDocs = async (acknowledge=false) => {
    setPendingLoading(true)
    try {
      const data = await deliveryAPI.getMyPendingDocs()
      const forms = data.forms || []
      const invoices = data.invoices || []
      setPendingForms(forms)
      setPendingInvoices(invoices)
      if (acknowledge) {
        setPendingDocsBadge(0)
      } else {
        const u = [...forms, ...invoices].filter(d => !d.readByUser).length
        setPendingDocsBadge(u)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setPendingLoading(false)
    }
  }

  const _sigGP=(el,e)=>{const r=el.getBoundingClientRect();const s=e.touches?e.touches[0]:e;return{x:(s.clientX-r.left)*(el.width/r.width),y:(s.clientY-r.top)*(el.height/r.height)}}
  const _sigInit=(el)=>{
    if(!el)return
    const ctx=el.getContext('2d');ctx.clearRect(0,0,el.width,el.height);ctx.fillStyle='#ffffff';ctx.fillRect(0,0,el.width,el.height)
    ctx.strokeStyle='#12224A';ctx.lineWidth=2.2;ctx.lineCap='round';ctx.lineJoin='round'
    const sd=(e)=>{e.preventDefault();sigDrawingRef.current=true;const p=_sigGP(el,e);sigLastPos.current=p;ctx.beginPath();ctx.moveTo(p.x,p.y)}
    const mv=(e)=>{e.preventDefault();if(!sigDrawingRef.current)return;const p=_sigGP(el,e);ctx.lineTo(p.x,p.y);ctx.stroke();sigLastPos.current=p}
    const ed=()=>{sigDrawingRef.current=false}
    el.addEventListener('mousedown',sd,{passive:false});el.addEventListener('mousemove',mv,{passive:false});el.addEventListener('mouseup',ed)
    el.addEventListener('touchstart',sd,{passive:false});el.addEventListener('touchmove',mv,{passive:false});el.addEventListener('touchend',ed)
  }
  const _sigClear=()=>{const el=sigCanvasRef.current;if(!el)return;const ctx=el.getContext('2d');ctx.clearRect(0,0,el.width,el.height);ctx.fillStyle='#ffffff';ctx.fillRect(0,0,el.width,el.height)}
  const _sigExport=()=>{const el=sigCanvasRef.current;if(!el)return null;const d=el.getContext('2d').getImageData(0,0,el.width,el.height).data;return Array.from(d).every((v,i)=>i%4===3?v===255:v===255)?null:el.toDataURL('image/png')}

  // ── PDF docs animaliers côté user — même style que generateDeliveryPDF ──────
  const _downloadAnimalDocPDF = async (doc, adminSig, userSig) => {
    const jsPDF = await _getJsPDFUser()
    const W=210,H=297,M=14
    const N=[30,58,120],G=[217,174,92],WH=[255,255,255],BD=[190,200,220]
    const DARK=[44,62,80],GREY=[108,117,125]
    const fmt=dt=>dt?new Date(dt).toLocaleDateString('fr-FR'):'\u2014'

    // Charger logo
    const logo=await _getLogoUser()

    // ── Header partagé ──
    const _header = async (jd) => {
      const hH=38,lZW=52,lS=30
      jd.setFillColor(...WH);jd.rect(0,0,lZW,hH,'F')
      jd.setFillColor(...N);jd.rect(lZW,0,W-lZW,hH,'F')
      jd.setFillColor(...G);jd.rect(lZW-1.5,0,2,hH,'F');jd.rect(0,hH,W,1,'F')
      if(logo){try{jd.addImage(logo,'JPEG',(lZW-lS)/2,(hH-lS)/2,lS,lS)}catch(e){}}
      jd.setTextColor(...WH);jd.setFontSize(12);jd.setFont('helvetica','bold');jd.text('ANIMAUX POUR ADOPTION',W-M,11,{align:'right'})
      jd.setTextColor(...G);jd.setFontSize(7.5);jd.setFont('helvetica','bold');jd.text('DE LA FRANCE',W-M,18,{align:'right'})
      jd.setTextColor(180,200,230);jd.setFontSize(6);jd.setFont('helvetica','normal')
      jd.text('123 Rue des Compagnons - 75001 Paris, France',W-M,25,{align:'right'})
      jd.text('T\u00e9l: +33 07 57 75 00 92  |  Email: animauxdecompagnie@animauxpouradoptiondelafrance.com',W-M,31,{align:'right'})
      return 38
    }

    // ── Footer partagé ──
    const _footer = (jd, t1, t2) => {
      jd.setFillColor(...G);jd.rect(0,H-22,W,1,'F')
      jd.setFillColor(...N);jd.rect(0,H-21,W,21,'F')
      jd.setTextColor(...WH);jd.setFontSize(7);jd.setFont('helvetica','bold');jd.text(t1,W/2,H-13,{align:'center'})
      jd.setTextColor(160,175,200);jd.setFontSize(6);jd.setFont('helvetica','normal');jd.text(t2,W/2,H-5,{align:'center'})
    }

    // ── Bloc info (drawBlk) ──
    const _blk = (jd,bx,bw,by,title,rows) => {
      const ROW=5.8,TH=7,PAD=5,bh=TH+rows.length*ROW+PAD
      jd.setFillColor(...N);jd.rect(bx,by,bw,TH,'F')
      jd.setTextColor(...WH);jd.setFontSize(7);jd.setFont('helvetica','bold');jd.text(title,bx+bw/2,by+4.8,{align:'center'})
      jd.setFillColor(...WH);jd.rect(bx,by+TH,bw,bh-TH,'F')
      jd.setDrawColor(...BD);jd.setLineWidth(0.3);jd.rect(bx,by+TH,bw,bh-TH)
      jd.setDrawColor(...N);jd.setLineWidth(0.4);jd.rect(bx,by,bw,bh)
      let ry=by+TH+5.5
      rows.forEach(([label,val])=>{
        jd.setTextColor(...N);jd.setFontSize(6.5);jd.setFont('helvetica','bold')
        const lbl=label+' :';jd.text(lbl,bx+5,ry);const lw2=jd.getTextWidth(lbl)
        jd.setFont('helvetica','normal');const vs=String(val||'\u2014').slice(0,32);jd.text(vs,bx+5+lw2+2,ry)
        const sx2=bx+5+lw2+3+jd.getTextWidth(vs)
        if(sx2<bx+bw-4){jd.setDrawColor(...BD);jd.setLineWidth(0.25);jd.setLineDashPattern([0.8,0.8],0);jd.line(sx2,ry-0.5,bx+bw-4,ry-0.5);jd.setLineDashPattern([],0)}
        ry+=ROW
      });return bh
    }

    // ════════════════════════════════════════
    // CAS : ATTESTATION VÉTÉRINAIRE
    // → rendu identique à l'admin (generateAttestationPDF)
    // ════════════════════════════════════════
    if(doc.docType==='attestation'){
      const jd = new jsPDF({ unit:'mm', format:'a4' })
      const MA=20
      const txt=(s,x,y2,o={})=>jd.text(String(s==null||s===''?'\u2014':s),x,y2,o)
      await _header(jd)
      let y=38+18
      // Titre
      jd.setTextColor(...N);jd.setFontSize(20);jd.setFont('helvetica','bold');txt('ATTESTATION V\u00c9T\u00c9RINAIRE',W/2,y,{align:'center'});y+=14
      // Lire le payload stocké
      const p = (()=>{try{return JSON.parse(doc.payload||'{}').d||{}}catch(e){return {}}})()
      // Intro
      jd.setTextColor(...DARK);jd.setFontSize(10);jd.setFont('helvetica','normal')
      const vetNom=p.vetNom||'\u2014';const ordre=p.vetNumOrdre||'\u2014'
      txt('Je soussign\u00e9(e), Dr. ',MA,y);const w1=jd.getTextWidth('Je soussign\u00e9(e), Dr. ')
      jd.setFont('helvetica','bold');txt(vetNom,MA+w1,y);const w2=jd.getTextWidth(vetNom)
      jd.setFont('helvetica','normal');txt(', v\u00e9t\u00e9rinaire inscrit(e) \u00e0 l\u2019Ordre National des V\u00e9t\u00e9rinaires',MA+w1+w2,y);y+=6
      txt('sous le num\u00e9ro ',MA,y);const w3=jd.getTextWidth('sous le num\u00e9ro ')
      jd.setFont('helvetica','bold');txt(ordre+',',MA+w3,y);const w4=jd.getTextWidth(ordre+',')
      jd.setFont('helvetica','normal');txt(' certifie avoir examin\u00e9 ce jour l\u2019animal ci-apr\u00e8s :',MA+w3+w4,y);y+=12
      // Champs animal
      const aFields=[
        ["Nom de l\u2019animal",p.animalNom||doc.animalNom||'\u2014'],
        ['Esp\u00e8ce',p.animalEspece||doc.animalEspece||'\u2014'],
        ['Race',p.animalRace||'\u2014'],['Sexe',p.animalSexe||'\u2014'],
        ['Couleur',p.animalCouleur||'\u2014'],['Puc\u00e9',p.animalPuce||'\u2014'],
        ["Propri\u00e9taire de l\u2019animal",p.proprietaireNom||doc.clientNom||'\u2014'],
        ['Identification',p.animalId||'\u2014'],
      ]
      aFields.forEach(([label,val])=>{
        jd.setFont('helvetica','bold');jd.setTextColor(...N);jd.setFontSize(10)
        const lbl=label+' : ';txt(lbl,MA+8,y);const lw2=jd.getTextWidth(lbl)
        jd.setFont('helvetica','normal');jd.setTextColor(...DARK);txt(val,MA+8+lw2,y);y+=7
      });y+=4
      // Attestations cochées
      jd.setTextColor(...DARK);jd.setFontSize(10);jd.setFont('helvetica','normal')
      txt('Apr\u00e8s examen clinique, j\u2019atteste que l\u2019animal est :',MA,y);y+=8
      ;['En bonne sant\u00e9 g\u00e9n\u00e9rale','\u00c0 jour de ses vaccinations obligatoires',"Ne pr\u00e9sente aucun signe de maladie contagieuse au moment de l\u2019examen",'Apte au transport auquel il est destin\u00e9'].forEach((label)=>{
        // Puce carrée remplie (fiable cross-version jsPDF)
        jd.setFillColor(30,58,120);jd.rect(MA+8,y-2.8,2.2,2.2,'F')
        jd.setTextColor(...DARK);jd.setFontSize(10);jd.setFont('helvetica','normal')
        txt(label,MA+13,y);y+=7
      });y+=8
      // Fait pour servir — NOIR
      jd.setTextColor(...DARK);jd.setFontSize(10);jd.setFont('helvetica','normal')
      txt('Fait pour servir et valoir ce que de droit.',MA,y);y+=16
      // ── Deux colonnes : gauche = lieu/cachet, droite = label signature/image ──
      const dateStr=p.date?new Date(p.date).toLocaleDateString('fr-FR'):(doc.date?new Date(doc.date).toLocaleDateString('fr-FR'):new Date().toLocaleDateString('fr-FR'))
      const halfW=(W-MA*2)/2
      const cxL=MA+halfW/2
      const cxR=MA+halfW+halfW/2
      // Labels sur la même ligne
      jd.setTextColor(...N);jd.setFont('helvetica','bold');jd.setFontSize(10)
      txt('Fait \u00e0 '+(p.ville||'\u2014')+', le '+dateStr,cxL,y,{align:'center'})
      jd.setTextColor(...GREY);jd.setFont('helvetica','italic')
      txt('Signature du v\u00e9t\u00e9rinaire',cxR,y,{align:'center'});y+=8
      // Cachet centré sous "Fait à..." dans la colonne gauche
      const sig=adminSig||doc.adminSignature||null
      const stamp=(()=>{try{return JSON.parse(doc.payload||'{}').stamp||null}catch(e){return null}})()
      const stampS=32
      if(stamp){try{jd.addImage(stamp,'PNG',cxL-stampS/2,y,stampS,stampS)}catch(e){}}
      // Signature centrée sous "Signature du vétérinaire" dans la colonne droite
      const sigW=55,sigH=24
      if(sig){try{jd.addImage(sig,'PNG',cxR-sigW/2,y+2,sigW,sigH)}catch(e){}}
      else{jd.setDrawColor(180,180,200);jd.setLineWidth(0.3);jd.line(cxR-sigW/2,y+sigH,cxR+sigW/2,y+sigH)}
      _footer(jd,'Attestation v\u00e9t\u00e9rinaire \u2014 Animaux pour Adoption de la France','Document officiel \u2014 \u00e0 conserver avec l\u2019animal')
      jd.save('attestation-vet-'+(doc.animalNom||'animal').replace(/\s+/g,'-')+'.pdf')
      return
    }

    // ════════════════════════════════════════
    // CAS : CARNET DE SANTE — rendu identique admin
    // ════════════════════════════════════════
    if(doc.docType==='carnet'){
      const p=(()=>{try{const parsed=JSON.parse(doc.payload||'{}');return {d:parsed.d||{},vaccins:parsed.vaccins||[],antiparas:parsed.antiparas||[],photo:parsed.photo||null,cachet:parsed.cachet||null}}catch(e){return {d:{},vaccins:[],antiparas:[],photo:null,cachet:null}}})()
      const d=p.d, vaccins=p.vaccins, antiparas=p.antiparas, photoData=p.photo, cachetData=p.cachet
      const sigData=adminSig||doc.adminSignature||null
      const jsPDF2 = await _getJsPDFUser()
      const jd=new jsPDF2({unit:'mm',format:'a4'})
      const W=210,H=297,M=12,CW=W-M*2
      const NV=[30,58,120],GD=[217,174,92],WH=[255,255,255],BD=[203,213,225]
      const NVlt=[235,240,250]
      const safe2=s=>{if(s==null||s==='')return '';return String(s).replace(/[\u2018\u2019]/g,"'").replace(/[\u201C\u201D]/g,'"').replace(/\u2014/g,'-').replace(/\u2013/g,'-').replace(/[^\u0000-\u00FF]/g,'')}
      const t=(s,x,yy,o={})=>jd.text(safe2(String(s==null?'':s))||' ',x,yy,o)
      const fmt=dt=>{if(!dt)return '';try{return new Date(dt).toLocaleDateString('fr-FR')}catch(e){return String(dt)}}
      const docRef=doc.docNumber||('CS-'+new Date().getFullYear()+'-'+(d.name||doc.animalNom||'ANI').replace(/\s+/g,'').slice(0,6).toUpperCase())

      const secBand=(label,x,yy,w,h=7)=>{jd.setFillColor(...NV);jd.rect(x,yy,w,h,'F');jd.setTextColor(...WH);jd.setFontSize(8.5);jd.setFont('helvetica','bold');t(label,x+4,yy+h-1.5)}
      const fld=(lbl,val,x,yy,w)=>{
        jd.setTextColor(...NV);jd.setFontSize(6.5);jd.setFont('helvetica','bold');t(lbl,x,yy)
        jd.setTextColor(0,0,0);jd.setFontSize(9);jd.setFont('helvetica','normal');t(safe2(String(val||'')).slice(0,Math.floor(w/1.5)),x,yy+7)
        jd.setDrawColor(...BD);jd.setLineWidth(0.2);jd.line(x,yy+9.5,x+w,yy+9.5)
      }

      const PAGE_BOTTOM2=H-12
      const drawFooter2=()=>{
        jd.setFillColor(...NV);jd.rect(0,H-10,W,10,'F')
        jd.setFillColor(...GD);jd.rect(0,H-10,W,0.8,'F')
        jd.setTextColor(180,205,235);jd.setFontSize(6.5);jd.setFont('helvetica','normal')
        t('Animaux pour Adoption de la France  -  Carnet de Sante  -  Document officiel a conserver',W/2,H-4,{align:'center'})
        jd.setTextColor(...GD);jd.setFontSize(6.5);jd.setFont('helvetica','bold');t(docRef,W-M,H-4,{align:'right'})
      }
      const drawContHeader2=()=>{
        jd.setFillColor(...NV);jd.rect(0,0,W,11,'F')
        jd.setFillColor(...GD);jd.rect(0,11,W,0.6,'F')
        jd.setTextColor(...GD);jd.setFontSize(7.5);jd.setFont('helvetica','bold');t('CARNET DE SANTE - Suite',M,7.5)
        jd.setTextColor(180,205,235);jd.setFontSize(6.5);jd.setFont('helvetica','normal');t(docRef,W-M,7.5,{align:'right'})
      }
      const chkPage2=(need)=>{if(y+need>PAGE_BOTTOM2){drawFooter2();jd.addPage();drawContHeader2();y=16}}

      let y=0

      // HEADER
      const hH=42
      jd.setFillColor(...NV);jd.rect(0,0,W,hH,'F')
      jd.setFillColor(...GD);jd.rect(0,0,2,hH,'F')
      jd.setTextColor(180,205,235);jd.setFontSize(8);jd.setFont('helvetica','normal');t('LE CARNET',W/2,13,{align:'center'})
      jd.setTextColor(255,255,255);jd.setFontSize(20);jd.setFont('helvetica','bold');t('DE SANTE',W/2,28,{align:'center'})
      jd.setFillColor(...GD);jd.rect(W/2-40,32,80,0.7,'F')
      jd.setFontSize(8);jd.setFont('helvetica','bold') // set font before measuring width
      const badgeText=docRef, badgeWidth=jd.getTextWidth(badgeText)+10
      try{jd.setFillColor(...GD);jd.roundedRect(W-M-badgeWidth,11,badgeWidth,9,2,2,'F')}catch(e){jd.setFillColor(...GD);jd.rect(W-M-badgeWidth,11,badgeWidth,9,'F')}
      jd.setTextColor(...NV);t(badgeText,W-M-badgeWidth/2,17.5,{align:'center'})
      jd.setFillColor(...GD);jd.rect(0,hH,W,1,'F')
      y=hH

      // BANDE NOM
      jd.setFillColor(252,246,226);jd.rect(0,y,W,10,'F')
      const nlb='DE:', nVal=safe2(d.name||doc.animalNom||'').toUpperCase()
      jd.setFontSize(7.5);jd.setFont('helvetica','bold');jd.setTextColor(...GD)
      const nlbW=jd.getTextWidth(nlb)
      jd.setFontSize(12);jd.setFont('helvetica','bold');jd.setTextColor(...NV)
      const nValW=jd.getTextWidth(nVal)
      const bStart=W/2-(nlbW+5+nValW)/2
      jd.setFontSize(7.5);jd.setFont('helvetica','bold');jd.setTextColor(...GD);t(nlb,bStart,y+7)
      jd.setFontSize(12);jd.setFont('helvetica','bold');jd.setTextColor(...NV);t(nVal,bStart+nlbW+5,y+7)
      jd.setFillColor(...GD);jd.rect(0,y+10,W,0.5,'F')
      y+=11

      // IDENTITE
      secBand('IDENTITE DE L\'ANIMAL',M,y,CW);y+=7
      const phW=46,phH=62
      const iX=M+phW+4,iW=CW-phW-4,hfw=(iW-6)/2
      const specStr=safe2(d.species||doc.animalEspece||'Animal').toUpperCase()+(d.breed?' - '+safe2(d.breed).toUpperCase():'')+(d.age?' ['+safe2(d.age)+']':'')
      // Photo and gold bar start at same y; bar is right-column only
      if(photoData){try{jd.addImage(photoData,'JPEG',M,y,phW,phH)}catch(e){try{jd.addImage(photoData,'PNG',M,y,phW,phH)}catch(e2){}}}
      else{
        jd.setFillColor(241,245,249);jd.rect(M,y,phW,phH,'F')
        jd.setDrawColor(...GD);jd.setLineWidth(0.5);jd.setLineDashPattern([2,1.5],0);jd.rect(M,y,phW,phH);jd.setLineDashPattern([],0)
        jd.setTextColor(...NV);jd.setFontSize(8);jd.setFont('helvetica','normal');t('PHOTO',M+phW/2,y+phH/2-4,{align:'center'});t('ANIMAL',M+phW/2,y+phH/2+4,{align:'center'})
      }
      jd.setFillColor(...GD);jd.rect(iX,y,iW,7.5,'F')
      jd.setTextColor(...NV);jd.setFontSize(8.5);jd.setFont('helvetica','bold');t(specStr,iX+4,y+5.5)
      let gy=y+7.5+3
      fld('Nom',safe2(d.name||''),iX,gy,hfw);fld('Espece',safe2(d.species||doc.animalEspece||''),iX+hfw+6,gy,hfw);gy+=14
      fld('Race',safe2(d.breed||''),iX,gy,hfw);fld('Age',safe2(d.age||''),iX+hfw+6,gy,hfw);gy+=14
      fld('Sexe',safe2(d.sex||''),iX,gy,hfw);fld('Sterilise(e)',safe2(d.sterilise||''),iX+hfw+6,gy,hfw);gy+=14
      fld('Poids (kg)',safe2(d.poids?String(d.poids)+' kg':''),iX,gy,hfw);fld('N\u00b0 Identification / Puce',safe2(d.identification||''),iX+hfw+6,gy,hfw)
      y+=phH+5

      // PROPRIETAIRE
      chkPage2(60)
      secBand('PROPRIETAIRE',M,y,CW);y+=7
      jd.setFillColor(248,250,252);jd.rect(M,y,CW,50,'F')
      const pw2=(CW-8)/2
      fld('Nom du proprietaire',safe2(d.proprietaire_nom||doc.clientNom||''),M+4,y+4,pw2);fld('Telephone',safe2(d.proprietaire_tel||''),M+pw2+8,y+4,pw2)
      fld('Email',safe2(d.proprietaire_email||doc.clientEmail||''),M+4,y+19,pw2);fld('Adresse',safe2(d.proprietaire_adresse||''),M+pw2+8,y+19,pw2)
      fld('Ville',safe2(d.proprietaire_ville||''),M+4,y+34,pw2);fld('Code postal',safe2(d.proprietaire_cp||''),M+pw2+8,y+34,pw2)
      y+=55

      // CABINET VETERINAIRE
      chkPage2(58)
      secBand('CABINET VETERINAIRE',M,y,CW);y+=7
      jd.setFillColor(248,250,252);jd.rect(M,y,CW,48,'F')
      fld('Nom du cabinet / Veterinaire traitant',safe2((d.veterinaire_cabinet||'')+(d.attestation_veterinaire?' - Dr. '+d.attestation_veterinaire:'')+(d.attestation_ordre?' (Ordre N\u00b0 '+d.attestation_ordre+')':'')),M+4,y+4,CW-8)
      const vc2=(CW-8)/2
      fld('Telephone',safe2(d.veterinaire_tel||''),M+4,y+19,vc2);fld('Email',safe2(d.veterinaire_email||''),M+vc2+8,y+19,vc2)
      fld('Adresse',safe2(d.veterinaire_adresse||''),M+4,y+34,CW-8)
      y+=52

      // VACCINATIONS
      if(vaccins&&vaccins.length>0){
        chkPage2(24+vaccins.length*8)
        const hdrs=['VACCIN','DATE','RAPPEL','N\u00b0 LOT','STATUT']
        const cols=[54,28,28,38,CW-148]
        const RH=8,THt=7,HH=6.5,totalH=THt+HH+vaccins.length*RH+2
        secBand('CARNET DE VACCINATIONS',M,y,CW)
        jd.setFillColor(255,255,255);jd.rect(M,y+THt,CW,totalH-THt,'F')
        let ty=y+THt
        jd.setFillColor(232,237,248);jd.rect(M,ty,CW,HH,'F')
        jd.setDrawColor(...BD);jd.setLineWidth(0.3);jd.line(M,ty+HH,M+CW,ty+HH)
        jd.setTextColor(...NV);jd.setFontSize(6.5);jd.setFont('helvetica','bold')
        let cx=M+3;hdrs.forEach((h,i)=>{t(h,cx,ty+4.5);cx+=cols[i]});ty+=HH
        vaccins.forEach((v,ri)=>{
          jd.setFillColor(ri%2===0?255:248,ri%2===0?255:250,ri%2===0?255:252);jd.rect(M,ty,CW,RH,'F')
          if(ri<vaccins.length-1){jd.setDrawColor(...BD);jd.setLineWidth(0.15);jd.line(M,ty+RH,M+CW,ty+RH)}
          jd.setTextColor(30,41,59);jd.setFontSize(8);jd.setFont('helvetica','normal')
          const ok=v.rappel&&new Date(v.rappel)>new Date()
          const row=[safe2(v.type||''),fmt(v.date),fmt(v.rappel),safe2(v.lot||''),v.rappel?null:'']
          let cx2=M+3
          row.forEach((cell,ci)=>{
            if(hdrs[ci]==='STATUT'&&v.rappel){jd.setFillColor(ok?220:254,ok?252:230,ok?220:210);jd.rect(cx2,ty+1.5,cols[ci]-4,RH-3,'F');jd.setTextColor(ok?22:150,ok?120:30,ok?60:40);jd.setFontSize(6.5);jd.setFont('helvetica','bold');t(ok?'A jour':'Rappel proche',cx2+(cols[ci]-4)/2,ty+5.5,{align:'center'});jd.setTextColor(30,41,59);jd.setFont('helvetica','normal');jd.setFontSize(8)}
            else{t(safe2(String(cell||'')).slice(0,26),cx2,ty+5.5)}
            cx2+=cols[ci]
          });ty+=RH
        })
        y+=totalH+5
      }

      // ANTIPARASITAIRES
      if(antiparas&&antiparas.length>0){
        chkPage2(24+antiparas.length*8)
        const hdrs2=['TYPE','PRODUIT','DATE','PROCHAINE ECHEANCE']
        const cols2=[34,64,34,CW-132]
        const RH2=8,THt2=7,HH2=6.5,totalH2=THt2+HH2+antiparas.length*RH2+2
        secBand('TRAITEMENTS ANTIPARASITAIRES',M,y,CW)
        jd.setFillColor(255,255,255);jd.rect(M,y+THt2,CW,totalH2-THt2,'F')
        let ty2=y+THt2
        jd.setFillColor(232,237,248);jd.rect(M,ty2,CW,HH2,'F')
        jd.setDrawColor(...BD);jd.setLineWidth(0.3);jd.line(M,ty2+HH2,M+CW,ty2+HH2)
        jd.setTextColor(...NV);jd.setFontSize(6.5);jd.setFont('helvetica','bold')
        let cx3=M+3;hdrs2.forEach((h,i)=>{t(h,cx3,ty2+4.5);cx3+=cols2[i]});ty2+=HH2
        antiparas.forEach((a,ri)=>{
          jd.setFillColor(ri%2===0?255:248,ri%2===0?255:250,ri%2===0?255:252);jd.rect(M,ty2,CW,RH2,'F')
          if(ri<antiparas.length-1){jd.setDrawColor(...BD);jd.setLineWidth(0.15);jd.line(M,ty2+RH2,M+CW,ty2+RH2)}
          jd.setTextColor(30,41,59);jd.setFontSize(8);jd.setFont('helvetica','normal')
          const row2=[safe2(a.type||''),safe2(a.produit||''),fmt(a.date),fmt(a.prochaine_date)]
          let cx4=M+3;row2.forEach((cell,ci)=>{t(safe2(String(cell||'')).slice(0,30),cx4,ty2+5.5);cx4+=cols2[ci]});ty2+=RH2
        })
        y+=totalH2+5
      }

      // EXAMEN CLINIQUE
      chkPage2(50)
      secBand('EXAMEN CLINIQUE - ATTESTATION VETERINAIRE',M,y,CW);y+=7
      const exH=36
      jd.setFillColor(255,255,255);jd.rect(M,y,CW,exH,'F')
      const ec1w=(CW-6)/3,ec2w=(CW-6)/3,ec3w=CW-6-ec1w-ec2w
      const ec1x=M+3,ec2x=ec1x+ec1w+3,ec3x=ec2x+ec2w+3
      jd.setDrawColor(...BD);jd.setLineWidth(0.2)
      jd.line(ec2x-1.5,y,ec2x-1.5,y+exH);jd.line(ec3x-1.5,y,ec3x-1.5,y+exH)
      fld('Date de l\'examen',fmt(d.attestation_date),ec1x,y+3,ec1w)
      fld('Validite',safe2(d.attestation_validite||''),ec1x,y+18,ec1w)
      fld('Veterinaire',safe2(d.attestation_veterinaire?'Dr. '+d.attestation_veterinaire:''),ec2x,y+3,ec2w)
      fld('N\u00b0 Ordre',safe2(d.attestation_ordre||''),ec2x,y+18,ec2w)
      jd.setTextColor(...NV);jd.setFontSize(6.5);jd.setFont('helvetica','bold');t('Observations medicales',ec3x,y+5)
      jd.setTextColor(0,0,0);jd.setFontSize(8.5);jd.setFont('helvetica','italic')
      const obsL2=jd.splitTextToSize(safe2(d.attestation_observations||''),ec3w);jd.text(obsL2.slice(0,3),ec3x,y+13)
      y+=exH+5

      // SIGNATURES & CACHET
      chkPage2(50)
      secBand('SIGNATURES & CACHET',M,y,CW);y+=7
      const sigH2=36,sigW2=(CW-6)/2,cX2=M+sigW2+6
      jd.setFillColor(250,251,253);jd.rect(M,y,sigW2,sigH2,'F')
      jd.setFillColor(250,251,253);jd.rect(cX2,y,sigW2,sigH2,'F')
      jd.setTextColor(...NV);jd.setFontSize(7);jd.setFont('helvetica','bold');t('SIGNATURE DU VETERINAIRE',M+sigW2/2,y+6,{align:'center'})
      jd.setDrawColor(...GD);jd.setLineWidth(0.5);jd.setLineDashPattern([2,1.5],0);jd.rect(M+6,y+9,sigW2-12,sigH2-18);jd.setLineDashPattern([],0)
      if(sigData){try{jd.addImage(sigData,'PNG',M+8,y+11,sigW2-16,sigH2-22)}catch(e){}}
      jd.setDrawColor(...BD);jd.setLineWidth(0.3);jd.line(M+6,y+sigH2-7,M+sigW2-6,y+sigH2-7)
      jd.setTextColor(100,116,139);jd.setFontSize(6.5);jd.setFont('helvetica','italic')
      t(safe2(d.attestation_veterinaire?'Dr. '+d.attestation_veterinaire+' - Veterinaire':'Veterinaire'),M+sigW2/2,y+sigH2-3,{align:'center'})
      jd.setTextColor(...NV);jd.setFontSize(7);jd.setFont('helvetica','bold');t('CACHET OFFICIEL',cX2+sigW2/2,y+6,{align:'center'})
      const cachetS=24
      if(cachetData){try{jd.addImage(cachetData,'PNG',cX2+sigW2/2-cachetS/2,y+10,cachetS,cachetS)}catch(e){}}
      else{jd.setDrawColor(...GD);jd.setLineWidth(0.5);jd.setLineDashPattern([2,1.5],0);jd.circle(cX2+sigW2/2,y+20,12,'S');jd.setLineDashPattern([],0)}
      jd.setTextColor(150,165,185);jd.setFontSize(6.5);jd.setFont('helvetica','italic');t('Cachet du cabinet',cX2+sigW2/2,y+sigH2-3,{align:'center'})

      drawFooter2()
      jd.save((doc.docNumber||'carnet-sante')+'.pdf')
      return
    }


    // CAS GÉNÉRIQUES : cession + identification
    // ════════════════════════════════════════
    // ── CAS : CESSION — rendu identique admin ──
if(doc.docType==='cession'){
  const p=(()=>{try{const parsed=JSON.parse(doc.payload||'{}');return parsed.d||parsed||{}}catch(e){return {}}})()
  const f={...p,...doc,
    adminSignature:adminSig||doc.adminSignature||null,
    userSignature:userSig||doc.userSignature||null
  }
  const jsPDF3 = await _getJsPDFUser()
  const jd3=new jsPDF3({unit:'mm',format:'a4'})
  const W3=210,H3=297,M3=14,CW3=W3-M3*2
  const safe3=s=>{if(s==null||s==='')return '\u2014';return String(s).replace(/[\u2018\u2019]/g,"'").replace(/[\u201C\u201D]/g,'"').replace(/\u2014/g,'-').replace(/[^\u0000-\u00FF]/g,'')}
  const t3=(s,x,y2,o={})=>jd3.text(safe3(String(s==null?'':s))||' ',x,y2,o)
  const fmt3=dt=>{if(!dt||dt==='\u2014')return '\u2014';try{return new Date(dt).toLocaleDateString('fr-FR')}catch(e){return String(dt)}}
  
  // Header
  const hH3=38,lZW3=50
  jd3.setFillColor(255,255,255);jd3.rect(0,0,lZW3,hH3,'F')
  jd3.setFillColor(30,58,120);jd3.rect(lZW3,0,W3-lZW3,hH3,'F')
  jd3.setFillColor(217,174,92);jd3.rect(lZW3-1,0,2,hH3,'F')
  jd3.setFillColor(217,174,92);jd3.rect(0,hH3,W3,0.8,'F')
  if(logo){try{jd3.addImage(logo,'JPEG',(lZW3-28)/2,(hH3-28)/2,28,28)}catch(e){}}
  jd3.setTextColor(255,255,255);jd3.setFontSize(11);jd3.setFont('helvetica','bold')
  t3('ANIMAUX POUR ADOPTION DE LA FRANCE',W3-M3,10,{align:'right'})
  jd3.setFontSize(8.5);jd3.setFont('helvetica','normal');jd3.setTextColor(180,200,230)
  t3(safe3(f.cedantAdresse||'123 Rue des Compagnons, 75001 Paris'),W3-M3,17,{align:'right'})
  t3('Tel : '+safe3(f.cedantTel||'+33 07 57 75 00 92'),W3-M3,23,{align:'right'})
  t3('Email : '+safe3(f.cedantEmail||'animauxdecompagnie@animauxpouradoptiondelafrance.com'),W3-M3,29,{align:'right'})
  
  let y3=hH3+10
  
  // Titre en NAVY
  jd3.setTextColor(30,58,120);jd3.setFontSize(20);jd3.setFont('helvetica','bold')
  t3('TRANSFERT DE DOCUMENTS',W3/2,y3,{align:'center'});y3+=6
  jd3.setFontSize(9);jd3.setFont('helvetica','normal');jd3.setTextColor(100,116,139)
  t3('Date : '+fmt3(f.date||doc.date),W3/2,y3,{align:'center'});y3+=6
  
  // Sections
  const drawIS3=(title,rows,y0)=>{
    jd3.setTextColor(217,174,92);jd3.setFontSize(9);jd3.setFont('helvetica','bold')
    t3(title.toUpperCase(),M3,y0);y0+=2
    jd3.setDrawColor(217,174,92);jd3.setLineWidth(0.2);jd3.line(M3,y0,M3+CW3,y0);y0+=5
    rows.forEach(([lbl,val])=>{
      jd3.setTextColor(18,34,74);jd3.setFontSize(9);jd3.setFont('helvetica','bold')
      t3(lbl+' :',M3,y0)
      jd3.setTextColor(30,41,59);jd3.setFont('helvetica','normal')
      t3(safe3(String(val||'\u2014')),50,y0)
      y0+=6.5
    })
    return y0+2
  }
  
  y3=drawIS3('INFORMATIONS DU CLIENT',[
    ['Nom',f.acquereurNom||doc.clientNom],
    ['Email',f.acquereurEmail||doc.clientEmail],
    ['T\u00e9l\u00e9phone',f.acquereurTel],
    ['Adresse',f.acquereurAdresse],
  ],y3)
  
  y3=drawIS3('INFORMATIONS DU PRESTATAIRE',[
    ['Soci\u00e9t\u00e9',f.cedantNom||'Animaux pour Adoption de la France'],
    ['T\u00e9l\u00e9phone',f.cedantTel||'\u2014'],
    ['Email',f.cedantEmail||'animauxdecompagnie@animauxpouradoptiondelafrance.com'],
    ['Adresse',f.cedantAdresse||'123 Rue des Compagnons, 75001 Paris'],
  ],y3)
  
  // Trait avant Objet
  y3 -= 4;
  jd3.setDrawColor(217,174,92);
  jd3.setLineWidth(0.2);
  jd3.line(M3, y3, M3 + CW3, y3);
  y3 += 9;
  
  // Corps
  jd3.setTextColor(18,34,74);jd3.setFontSize(10);jd3.setFont('helvetica','bold')
  t3('Objet : Confirmation de transfert de documents',M3,y3);y3+=10
  jd3.setFont('helvetica','normal');jd3.setTextColor(30,41,59)
  t3('Madame, Monsieur '+safe3(f.acquereurNom||doc.clientNom||'')+',',M3,y3);y3+=7
  const paras3=['Par la pr\u00e9sente, la soci\u00e9t\u00e9 '+(f.cedantNom||'Animaux pour Adoption de la France')+' confirme le transfert des documents relatifs au dossier de cession de votre animal.','Ce document atteste de la remise de l\'ensemble des pi\u00e8ces n\u00e9cessaires \u00e0 la constitution de votre dossier de cession animalier.','Nous vous prions de bien vouloir v\u00e9rifier l\'exactitude des informations mentionn\u00e9es ci-dessus et de nous signaler toute anomalie dans les plus brefs d\u00e9lais.']
  paras3.forEach(para=>{const lines3=jd3.splitTextToSize(para,CW3);jd3.text(lines3,M3,y3);y3+=lines3.length*3.8+2})
  
  // Tableau
  jd3.setTextColor(217,174,92);jd3.setFontSize(9);jd3.setFont('helvetica','bold')
  t3('LISTE DES DOCUMENTS TRANSFERES',M3,y3);y3+=2
  jd3.setDrawColor(217,174,92);jd3.setLineWidth(0.2);jd3.line(M3,y3,M3+CW3,y3);y3+=3
  const cols3=[8,72,20,42,24],hdrs3=['N°','Document','Nb','Référence','Date'],RH3=8
  jd3.setFillColor(30,58,120);jd3.rect(M3,y3,CW3,RH3,'F')
  jd3.setTextColor(255,255,255);jd3.setFontSize(7.5);jd3.setFont('helvetica','bold')
  let cx3=M3+2;hdrs3.forEach((h,i)=>{t3(h,cx3,y3+5.5);cx3+=cols3[i]});y3+=RH3
  
  const docList3=Array.isArray(f.documents)&&f.documents.length>0
    ? f.documents
    : [{document:'Attestation de cession',reference:f.animalId||'\u2014',nombre:'1',date:f.date||doc.date}]
  const docRows3=docList3.map((row,ri)=>[String(ri+1),safe3(row.document||''),safe3(row.nombre||'1'),safe3(row.reference||''),fmt3(row.date)])
  
  const pageBottom3=H3-10
  const drawHeader3=()=>{
    jd3.setFillColor(255,255,255);jd3.rect(0,0,lZW3,hH3,'F')
    jd3.setFillColor(30,58,120);jd3.rect(lZW3,0,W3-lZW3,hH3,'F')
    jd3.setFillColor(217,174,92);jd3.rect(lZW3-1,0,2,hH3,'F')
    jd3.setFillColor(217,174,92);jd3.rect(0,hH3,W3,0.8,'F')
    if(logo){try{jd3.addImage(logo,'JPEG',(lZW3-28)/2,(hH3-28)/2,28,28)}catch(e){}}
    jd3.setTextColor(255,255,255);jd3.setFontSize(11);jd3.setFont('helvetica','bold')
    t3('ANIMAUX POUR ADOPTION DE LA FRANCE',W3-M3,10,{align:'right'})
    jd3.setFontSize(8.5);jd3.setFont('helvetica','normal');jd3.setTextColor(180,200,230)
    t3(safe3(f.cedantAdresse||'123 Rue des Compagnons, 75001 Paris'),W3-M3,17,{align:'right'})
    t3('Tel : '+safe3(f.cedantTel||'+33 07 57 75 00 92'),W3-M3,23,{align:'right'})
    t3('Email : '+safe3(f.cedantEmail||'animauxdecompagnie@animauxpouradoptiondelafrance.com'),W3-M3,29,{align:'right'})
    jd3.setFillColor(18,34,74);jd3.rect(0,H3-10,W3,10,'F')
    jd3.setFillColor(217,174,92);jd3.rect(0,H3-10,W3,0.8,'F')
    jd3.setTextColor(180,200,230);jd3.setFontSize(6.5);jd3.setFont('helvetica','normal')
    t3('ANIMAUX POUR ADOPTION DE LA FRANCE - '+(f.cedantAdresse||'123 Rue des Compagnons, 75001 Paris'),W3/2,H3-4,{align:'center'})
  }
  docRows3.forEach((row,ri)=>{
    if(y3+RH3>pageBottom3){jd3.addPage();drawHeader3();y3=hH3+10}
    const bg=ri%2===0
    jd3.setFillColor(bg?255:248,bg?255:250,bg?255:252);jd3.rect(M3,y3,CW3,RH3,'F')
    jd3.setDrawColor(203,213,225);jd3.setLineWidth(0.15);jd3.rect(M3,y3,CW3,RH3)
    jd3.setTextColor(30,41,59);jd3.setFontSize(7.5);jd3.setFont('helvetica','normal')
    let cx4=M3+2;row.forEach((cell,ci)=>{t3(safe3(cell||'').slice(0,ci===1?45:18),cx4,y3+5.5);cx4+=cols3[ci]})
    
    // Traits verticaux du tableau
    let xAcc=M3;
    cols3.forEach((colWidth)=>{
      xAcc+=colWidth;
      jd3.setDrawColor(203,213,225);
      jd3.setLineWidth(0.15);
      jd3.line(xAcc,y3,xAcc,y3+RH3);
    });
    
    y3+=RH3
  });
  y3+=6
  
  // Section signatures — saut de page seulement si pas assez de place
  if(y3+55>H3-12){jd3.addPage();drawHeader3();y3=hH3+10}

  // Signatures
  const sigW3=(CW3-6)/2,sigX23=M3+sigW3+6,sigH3=32
  jd3.setFontSize(9);jd3.setFont('helvetica','bold');jd3.setTextColor(30,41,59)
  t3('Signature du Prestataire',M3+sigW3/2,y3,{align:'center'})
  t3('Signature du Client',sigX23+sigW3/2,y3,{align:'center'});y3+=6
  jd3.setFillColor(248,250,252);jd3.rect(M3,y3,sigW3,sigH3,'F')
  jd3.setDrawColor(203,213,225);jd3.setLineWidth(0.15);jd3.rect(M3,y3,sigW3,sigH3)
  if(f.adminSignature){try{jd3.addImage(f.adminSignature,'PNG',M3+3,y3+2,sigW3-6,sigH3-8)}catch(e){}}
  jd3.setDrawColor(100,116,139);jd3.setLineWidth(0.15);jd3.line(M3+3,y3+sigH3-8,M3+sigW3-3,y3+sigH3-8)
  jd3.setTextColor(30,41,59);jd3.setFontSize(7.5);jd3.setFont('helvetica','normal')
  t3(safe3(f.cedantNom||'Animaux pour Adoption de la France'),M3+3,y3+sigH3-4)
  jd3.setFillColor(248,250,252);jd3.rect(sigX23,y3,sigW3,sigH3,'F')
  jd3.setDrawColor(203,213,225);jd3.setLineWidth(0.15);jd3.rect(sigX23,y3,sigW3,sigH3)
  if(f.userSignature){try{jd3.addImage(f.userSignature,'PNG',sigX23+3,y3+2,sigW3-6,sigH3-8)}catch(e){}}
  else{jd3.setTextColor(148,163,184);jd3.setFontSize(7.5);jd3.setFont('helvetica','italic');t3('En attente de signature',sigX23+sigW3/2,y3+sigH3/2,{align:'center'})}
  jd3.setDrawColor(100,116,139);jd3.setLineWidth(0.15);jd3.line(sigX23+3,y3+sigH3-8,sigX23+sigW3-3,y3+sigH3-8)
  jd3.setTextColor(30,41,59);jd3.setFontSize(7.5);jd3.setFont('helvetica','normal')
  t3(safe3(f.acquereurNom||doc.clientNom||'Client'),sigX23+3,y3+sigH3-4);y3+=sigH3+8
  t3(safe3(f.cedantNom||'Animaux pour Adoption de la France'),M3,y3)
  t3(safe3(f.cedantAdresse||''),M3,y3+5)
  t3(safe3(f.acquereurNom||doc.clientNom||''),sigX23,y3)
  t3(safe3(f.acquereurAdresse||''),sigX23,y3+5);y3+=16
  
  // Footer (conservé identique)
  jd3.setFillColor(18,34,74);jd3.rect(0,H3-10,W3,10,'F')
  jd3.setFillColor(217,174,92);jd3.rect(0,H3-10,W3,0.8,'F')
  jd3.setTextColor(180,200,230);jd3.setFontSize(6.5);jd3.setFont('helvetica','normal')
  t3('ANIMAUX POUR ADOPTION DE LA FRANCE - '+(f.cedantAdresse||'123 Rue des Compagnons, 75001 Paris'),W3/2,H3-4,{align:'center'})
  jd3.save((doc.docNumber||'cession')+'.pdf')
  return
}

    // ── CAS : IDENTIFICATION ──
    if(doc.docType==='identification'){
      const p=(()=>{try{const parsed=JSON.parse(doc.payload||'{}');return parsed.d||parsed||{}}catch(e){return {}}})()
      const d={...p,...doc}
      const jsPDF4 = await _getJsPDFUser()
      const jd4=new jsPDF4({unit:'mm',format:'a4'})
      const W4=210,H4=297,M4=13,CW4=W4-M4*2
      const safe4=s=>{if(s==null||s==='')return '';return String(s).replace(/[\u2018\u2019]/g,"'").replace(/[\u201C\u201D]/g,'"').replace(/\u2014/g,'-').replace(/[^\u0000-\u00FF]/g,'')}
      const t4=(s,x,y2,o={})=>jd4.text(safe4(String(s==null?'':s))||' ',x,y2,o)
      const fmt4=dt=>{if(!dt)return '';try{return new Date(dt).toLocaleDateString('fr-FR')}catch(e){return String(dt)}}
      let y4=0
      // Header
      jd4.setFillColor(30,58,122);jd4.rect(0,0,W4,32,'F')
      jd4.setTextColor(255,255,255);jd4.setFontSize(11);jd4.setFont('helvetica','bold')
      t4('SOCIETE D\'IDENTIFICATION DES CARNIVORES DOMESTIQUES',W4/2,9,{align:'center'})
      jd4.setFontSize(7.5);jd4.setFont('helvetica','normal')
      t4('112-114 Avenue Gabriel Peri  -  94246 L\'HAY LES ROSES Cedex',W4/2,15,{align:'center'})
      jd4.setFontSize(9.5);jd4.setFont('helvetica','bold')
      t4('0 810 778 778',W4/2,21.5,{align:'center'})
      jd4.setFontSize(7);jd4.setFont('helvetica','normal');jd4.setTextColor(180,200,235)
      t4(new Date().toLocaleDateString('fr-FR')+' '+new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}),W4/2,27.5,{align:'center'})
      y4=32+8
      // Intro
      jd4.setTextColor(71,85,105);jd4.setFontSize(8.5);jd4.setFont('helvetica','normal')
      t4('Madame, Monsieur,',M4,y4);y4+=4.5
      const intro4='Nous avons le plaisir de vous adresser la carte d\'identification de votre animal suite a l\'enregistrement de son identification et de vos coordonnees dans le Fichier National des Carnivores Domestiques (chiens, chats, furets).'
      const iLines4=jd4.splitTextToSize(intro4,CW4);jd4.text(iLines4,M4,y4);y4+=iLines4.length*3.9+7
      // Section helper
      const TH4=8,FH4=10.5,sec4=(title,rows,y0)=>{
        let sH=TH4;let ri=0;while(ri<rows.length){sH+=FH4;if(rows[ri]&&rows[ri][2]==='full'){ri++}else{ri+=2}}
        jd4.setFillColor(255,255,255);jd4.rect(M4,y0,CW4,sH,'F')
        jd4.setDrawColor(203,213,225);jd4.setLineWidth(0.3);jd4.rect(M4,y0,CW4,sH)
        jd4.setFillColor(241,245,249);jd4.rect(M4,y0,CW4,TH4,'F')
        jd4.setFillColor(52,120,200);jd4.rect(M4,y0,3,TH4,'F')
        jd4.setDrawColor(203,213,225);jd4.setLineWidth(0.25);jd4.line(M4,y0+TH4,M4+CW4,y0+TH4)
        jd4.setTextColor(30,41,59);jd4.setFontSize(7.5);jd4.setFont('helvetica','bold')
        t4(title.toUpperCase(),M4+6,y0+5.8)
        const cW4=CW4/2,c24=M4+cW4;let ry=y0+TH4
        const fld4=(lbl,val,fx,fw,fy)=>{
          jd4.setTextColor(22,163,74);jd4.setFontSize(6);jd4.setFont('helvetica','bold');t4(lbl,fx+3,fy+3.5)
          jd4.setFillColor(241,245,249);jd4.rect(fx+2,fy+4,fw-4,5.5,'F')
          jd4.setTextColor(30,41,59);jd4.setFontSize(7.5);jd4.setFont('helvetica','normal');t4(safe4(val||''),fx+4,fy+8.5)
        }
        let ri2=0;while(ri2<rows.length){
          const r=rows[ri2];if(!r){ri2++;continue}
          if(r[2]==='full'){fld4(r[0],r[1],M4,CW4,ry);ry+=FH4;ri2++;if(ri2<rows.length){jd4.setDrawColor(203,213,225);jd4.setLineWidth(0.2);jd4.line(M4,ry,M4+CW4,ry)}}
          else{fld4(r[0],r[1],M4,cW4,ry);jd4.setDrawColor(203,213,225);jd4.setLineWidth(0.2);jd4.line(c24,ry,c24,ry+FH4);if(rows[ri2+1]){fld4(rows[ri2+1][0],rows[ri2+1][1],c24,cW4,ry)}ry+=FH4;ri2+=2;if(ri2<rows.length){jd4.setDrawColor(203,213,225);jd4.setLineWidth(0.2);jd4.line(M4,ry,M4+CW4,ry)}}
        }
        return y0+sH+3
      }
      y4=sec4('Identification de l\'animal',[['Nom et prenom du proprietaire',d.ownerName||doc.clientNom||''],['Adresse complete',d.ownerAddress||''],['Tel 1',d.phone1||''],['E-mail',d.email||doc.clientEmail||''],['Veterinaire traitant',d.veterinaireNom?(safe4(d.veterinaireNom)+(d.veterinaireContact?' - '+safe4(d.veterinaireContact):'')):'','full']],y4)
      y4=sec4('Identification de l\'animal - puce',[['No identification',d.animalId||''],['Date',fmt4(d.idDate)],['Emplacement',d.idLocation||'','full']],y4)
      y4=sec4('Description du '+(d.animalEspece||doc.animalEspece||'chat').toLowerCase(),[['Date de naissance',fmt4(d.birthDate)],['Race',d.breed||''],['Couleur',d.coat||''],['Poil',d.hairType||''],['Sexe',d.sex==='MALE'?'Male':(d.sex||'')],['Sterilise',d.sterilise||'Non'],['Nom d\'usage',d.animalName||doc.animalNom||''],['Pays d\'origine',d.paysOrigine||'France']],y4)
      y4+=8
      // Footer
      jd4.setFillColor(248,250,252);jd4.rect(0,H4-8,W4,8,'F')
      jd4.setDrawColor(203,213,225);jd4.setLineWidth(0.3);jd4.line(0,H4-8,W4,H4-8)
      jd4.setTextColor(148,163,184);jd4.setFontSize(6.5);jd4.setFont('helvetica','normal')
      t4('Carte generee le '+new Date().toLocaleDateString('fr-FR')+' - Document informatif',W4/2,H4-3,{align:'center'})
      jd4.save((doc.docNumber||'identification')+'.pdf')
      return
    }
  }

  const generateDeliveryPDF = async (f, type) => {
    const jsPDF = await _getJsPDFUser()
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const W=210, H=297, M=14
    const N=[30,58,120], G=[217,174,92], WH=[255,255,255]
    const BK=[30,58,120], SL=[80,100,140], BD=[190,200,220]
    const fc=(...c)=>doc.setFillColor(...c)
    const tc=(...c)=>doc.setTextColor(...c)
    const dc=(...c)=>doc.setDrawColor(...c)
    const lw=w=>doc.setLineWidth(w)
    const txt=(s,x,y,o={})=>doc.text(String(s==null||s===''?'\u2014':s),x,y,o)

    const logo=await _getLogoUser()

    const isForm = type==='form'

    // ═══ HEADER ═══
    const hH=38, lZW=52, lS=30
    fc(...WH);doc.rect(0,0,lZW,hH,'F')
    fc(...N);doc.rect(lZW,0,W-lZW,hH,'F')
    fc(...G);doc.rect(lZW-1.5,0,2,hH,'F')
    fc(...G);doc.rect(0,hH,W,1,'F')
    if(logo){try{doc.addImage(logo,'JPEG',(lZW-lS)/2,(hH-lS)/2,lS,lS)}catch(e){}}
    tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold')
    txt('ANIMAUX POUR ADOPTION',W-M,11,{align:'right'})
    tc(...G);doc.setFontSize(7.5);doc.setFont('helvetica','bold')
    txt('DE LA FRANCE',W-M,18,{align:'right'})
    tc(180,200,230);doc.setFontSize(6);doc.setFont('helvetica','normal')
    txt('123 Rue des Compagnons - 75001 Paris, France',W-M,25,{align:'right'})
    txt('T\u00e9l: +33 07 57 75 00 92  |  Email: animauxdecompagnie@animauxpouradoptiondelafrance.com',W-M,31,{align:'right'})

    let y = isForm ? hH+2 : 42

    if(isForm) {
      // ═══ TITRE BON DE LIVRAISON ═══
      fc(...N);doc.rect(M,y,W-M*2,10,'F')
      fc(...G);doc.rect(M,y,3,10,'F')
      tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold')
      txt('BON DE LIVRAISON',W/2,y+6.8,{align:'center'})
      y+=13

      // ═══ ÉMETTEUR | CLIENT ═══
      const colW=(W-M*2)/2-2, col1x=M, col2x=M+colW+4
      const ROW_C=5.8, BLK_TITLE=7, BLK_PAD=7
      const BLK_TOT=BLK_TITLE+5*ROW_C+BLK_PAD
      const drawInfoBlock=(bx,bw,by,title,rows)=>{
        fc(...N);doc.rect(bx,by,bw,BLK_TITLE,'F')
        tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
        txt(title,bx+bw/2,by+4.8,{align:'center'})
        fc(...WH);doc.rect(bx,by+BLK_TITLE,bw,BLK_TOT-BLK_TITLE,'F')
        dc(...BD);lw(0.3);doc.rect(bx,by+BLK_TITLE,bw,BLK_TOT-BLK_TITLE)
        dc(...N);lw(0.4);doc.rect(bx,by,bw,BLK_TOT)
        let ry=by+BLK_TITLE+5.5
        rows.forEach(([label,val])=>{
          tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
          const lbl=label+' :';doc.text(lbl,bx+5,ry);const lblW=doc.getTextWidth(lbl)
          tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
          const valStr=String(val||'\u2014').slice(0,28);txt(valStr,bx+5+lblW+2,ry)
          const vw=doc.getTextWidth(valStr),sx=bx+5+lblW+3+vw
          if(sx<bx+bw-4){dc(...BD);doc.setLineWidth(0.25);doc.setLineDashPattern([0.8,0.8],0);doc.line(sx,ry-0.5,bx+bw-4,ry-0.5);doc.setLineDashPattern([],0)}
          ry+=ROW_C
        })
      }
      drawInfoBlock(col1x,colW,y,'\u00c9METTEUR',[
        ['Raison sociale',f.entreprise||'Animaux pour Adoption de la France'],
        ['Adresse',f.localisation||'123 Rue des Compagnons, 75001 Paris'],
        ['T\u00e9l\u00e9phone',f.tel||'+33 07 57 75 00 92'],
        ['Email','animauxdecompagnie@animauxpouradoptiondelafrance.com'],
      ])
      drawInfoBlock(col2x,colW,y,'CLIENT / DESTINATAIRE',[
        ['Nom',f.clientNom||'\u2014'],['Adresse',f.clientAdresse||'\u2014'],
        ['T\u00e9l\u00e9phone',f.clientTel||'\u2014'],['Email',f.clientEmail||'\u2014'],['R\u00e9f\u00e9rence',f.formNumber||'\u2014'],
      ])
      y+=BLK_TOT+3

      // ═══ INFORMATIONS DE LIVRAISON ═══
      fc(...N);doc.rect(M,y,W-M*2,7,'F');fc(...G);doc.rect(M,y,3,7,'F')
      tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt('INFORMATIONS DE LIVRAISON',W/2,y+4.8,{align:'center'});y+=7
      const infoFields=[['N\u00b0 de livraison',f.formNumber||'\u2014'],['Date',f.date||'\u2014'],['Heure pr\u00e9vue',f.heure||'\u2014'],['Date de commande',f.dateCommande||f.date||'\u2014'],['Mode de livraison',f.modeTransport||'Transport terrestre'],['Transporteur',f.responsable||'\u2014']]
      const infoLineH=4.5, infoBodyH=6*infoLineH+5
      fc(...WH);doc.rect(M,y,W-M*2,infoBodyH,'F');dc(...BD);lw(0.3);doc.rect(M,y,W-M*2,infoBodyH)
      let iy=y+5
      infoFields.forEach(([label,val])=>{
        tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','bold');const lbl=label+' :';doc.text(lbl,M+4,iy);const lblW=doc.getTextWidth(lbl)
        tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal');const iVal=String(val||'\u2014');txt(iVal,M+4+lblW+2,iy)
        const ivw=doc.getTextWidth(iVal),isx=M+4+lblW+3+ivw
        if(isx<M+W-M*2-4){dc(...BD);doc.setLineWidth(0.25);doc.setLineDashPattern([0.8,0.8],0);doc.line(isx,iy-0.5,M+W-M*2-4,iy-0.5);doc.setLineDashPattern([],0)}
        iy+=infoLineH
      })
      y+=infoBodyH+3

      // ═══ TABLEAU ANIMAUX ═══
      const cols=[{label:'N\u00b0',w:9},{label:'D\u00e9signation / Animal',w:38},{label:'Esp\u00e8ce',w:23},{label:'Race',w:23},{label:'\u00c2ge',w:13},{label:'Poids',w:15},{label:'Couleur / Signe',w:32},{label:'N\u00b0 Puce',w:29}]
      const tblW=W-M*2;fc(...N);doc.rect(M,y,tblW,7,'F');tc(...WH);doc.setFontSize(5.5);doc.setFont('helvetica','bold')
      let cx=M;cols.forEach(c=>{txt(c.label,cx+c.w/2,y+4.8,{align:'center'});cx+=c.w});y+=7
      const AROW_H=7
      const animalRows=[{num:1,animal:f.animal,espece:f.espece,race:f.race,age:f.ageAnimal,poids:f.poids,couleur:f.couleur,puce:f.puce},{},{},{},{}]
      animalRows.forEach(row=>{
        fc(...WH);doc.rect(M,y,tblW,AROW_H,'F');dc(...BD);lw(0.2);doc.rect(M,y,tblW,AROW_H)
        if(row.num){tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal');const vals=[row.num,row.animal||'',row.espece||'',row.race||'',row.age||'',row.poids||'',row.couleur||'',row.puce||''];cx=M;cols.forEach(c=>{txt(String(vals[cols.indexOf(c)]||''),cx+c.w/2,y+4.8,{align:'center'});cx+=c.w})}
        cx=M;dc(...BD);lw(0.2);cols.slice(0,-1).forEach(c=>{cx+=c.w;doc.line(cx,y,cx,y+AROW_H)});y+=AROW_H
      })
      y+=3

      // ═══ CONDITIONS DE LIVRAISON ═══
      fc(...N);doc.rect(M,y,W-M*2,7,'F');fc(...G);doc.rect(M,y,3,7,'F')
      tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt('CONDITIONS DE LIVRAISON ET OBSERVATIONS',W/2,y+4.8,{align:'center'});y+=7
      const condBodyH=7*5+6;fc(...WH);doc.rect(M,y,W-M*2,condBodyH,'F');dc(...BD);lw(0.3);doc.rect(M,y,W-M*2,condBodyH)
      const conditions=['Animal en bonne sant\u00e9 \u00e0 la livraison','Carnet de vaccination remis',"Documents d'identification remis (passeport, puce)",'Kit de bienvenue remis au client','Contrat de cession sign\u00e9','Informations de suivi post-adoption fournies','Animal conforme \u00e0 la demande du client']
      let cy=y+5;conditions.forEach(cond=>{dc(...BD);lw(0.35);doc.rect(M+5,cy-2.8,3.5,3.5);dc(...G);lw(0.6);doc.line(M+5.3,cy-1.2,M+6.1,cy-0.4);doc.line(M+6.1,cy-0.4,M+8.2,cy-3.0);tc(...N);doc.setFontSize(7);doc.setFont('helvetica','normal');txt(cond,M+11,cy);cy+=5});y+=condBodyH+3

    } else {
      // ═══ FACTURE ═══
      const fa=n=>Number(n||0).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})+' \u20AC'
      fc(...N);doc.rect(M,y,W-M*2,9,'F');tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold');txt('FACTURE',W/2,y+6.3,{align:'center'});y+=12
      tc(...BK);doc.setFontSize(7);doc.setFont('helvetica','bold')
      txt('N\u00b0 Facture :',M,y+4.5);txt(f.invoiceNumber||f.ref||'',M+26,y+4.5)
      dc(...BD);lw(0.35);doc.line(M+26,y+5.2,W/2-4,y+5.2);txt('Date :',W/2+4,y+4.5);txt(f.date||'',W/2+16,y+4.5);doc.line(W/2+16,y+5.2,W-M,y+5.2);y+=9
      const colW=(W-M*2)/2-2, col2x=M+colW+4, FH=6, BLTIT=8
      const drawFBlock=(bx,bw,by,title,rows)=>{
        fc(...N);doc.rect(bx,by,bw,BLTIT,'F');tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt(title,bx+bw/2,by+5.5,{align:'center'})
        const bodyH=rows.length*FH+3;fc(...WH);doc.rect(bx,by+BLTIT,bw,bodyH,'F');dc(...BD);lw(0.3);doc.rect(bx,by,bw,BLTIT+bodyH)
        let ry=by+BLTIT+FH
        rows.forEach(([label,val])=>{
          tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold');const lbl=label+' :';txt(lbl,bx+4,ry);const lw2=doc.getTextWidth(lbl)
          tc(...SL);doc.setFontSize(6.5);doc.setFont('helvetica','normal');const valStr=String(val||'').slice(0,26);txt(valStr,bx+4+lw2+2,ry)
          dc(...BD);doc.setLineWidth(0.25);doc.setLineDashPattern([0.8,0.8],0);const vw=doc.getTextWidth(valStr),sx=bx+4+lw2+3+vw;if(sx<bx+bw-3)doc.line(sx,ry-0.5,bx+bw-3,ry-0.5);doc.setLineDashPattern([],0);ry+=FH
        })
      }
      drawFBlock(M,colW,y,'\u00c9METTEUR (PRESTATAIRE)',[
        ['Raison sociale',f.entreprise||'Animaux pour Adoption'],['Adresse',f.localisation||''],['T\u00e9l\u00e9phone',f.tel||''],['Email','animauxdecompagnie@animauxpouradoptiondelafrance.com']
      ])
      drawFBlock(col2x,colW,y,'CLIENT (FACTUR\u00c9 \u00c0)',[
        ['Nom complet',f.clientNom||''],['Adresse',f.clientAdresse||''],['T\u00e9l\u00e9phone',f.clientTel||''],['Email',f.clientEmail||'']
      ])
      y+=BLTIT+4*FH+3+3
      const tC=[{l:'N\u00b0',w:10,a:'left'},{l:'D\u00e9signation',w:57,a:'left'},{l:'Esp\u00e8ce / Race',w:38,a:'left'},{l:'Qt\u00e9',w:15,a:'center'},{l:'P.U. (\u20AC)',w:30,a:'right'},{l:'Total (\u20AC)',w:32,a:'right'}]
      const tW=W-M*2;fc(...N);doc.rect(M,y,tW,7,'F');tc(...WH);doc.setFontSize(6);doc.setFont('helvetica','bold')
      let cx=M;tC.forEach(c=>{const tx=c.a==='right'?cx+c.w-2:c.a==='center'?cx+c.w/2:cx+3;txt(c.l,tx,y+4.8,{align:c.a});cx+=c.w});y+=7
      const pu=Number(f.montant||f.prixTransport||0),anStr=[(f.espece||''),(f.race||'')].filter(Boolean).join(' / ').slice(0,18)
      const dr=['1',String(f.description||'Transport animalier').slice(0,24),anStr||'','1',fa(pu),fa(pu)]
      const RH=6
      for(let ri=0;ri<5;ri++){const rowY=y+ri*RH;fc(ri%2===0?255:249,ri%2===0?255:250,ri%2===0?255:253);doc.rect(M,rowY,tW,RH,'F');dc(...BD);lw(0.2);doc.rect(M,rowY,tW,RH);if(ri===0){tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal');let rxC=M;tC.forEach((col,ci)=>{const tx=col.a==='right'?rxC+col.w-2:col.a==='center'?rxC+col.w/2:rxC+3;txt(dr[ci],tx,rowY+4.2,{align:col.a});rxC+=col.w})};let rxC2=M;dc(...BD);lw(0.18);tC.slice(0,-1).forEach(col=>{rxC2+=col.w;doc.line(rxC2,rowY,rxC2,rowY+RH)})}
      y+=5*RH+2
      const totW=90,totX=M+tW-totW
      fc(...N);doc.rect(totX,y+12,totW,11,'F');dc(...BD);lw(0.3);doc.rect(totX,y,totW,23);tc(...G);doc.setFontSize(7.5);doc.setFont('helvetica','bold');txt('TOTAL PAYE :',totX+4,y+19);tc(...WH);txt(fa(pu),totX+totW-4,y+19,{align:'right'});y+=23+3
      const mpW=(W-M*2)*0.6;fc(...N);doc.rect(M,y,mpW,7,'F');tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt('MODE DE PAIEMENT',M+mpW/2,y+4.8,{align:'center'});y+=7
      fc(...WH);doc.rect(M,y,mpW,18,'F');dc(...BD);lw(0.3);doc.rect(M,y,mpW,18)
      tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold');txt('IBAN :',M+4,y+5.5);doc.setFont('helvetica','normal');txt(f.iban||'',M+15,y+5.5);dc(...BD);lw(0.4);doc.line(M+15,y+6,M+mpW-4,y+6)
      doc.setFont('helvetica','bold');txt('BIC :',M+4,y+11);doc.setFont('helvetica','normal');txt(f.bic||'',M+13,y+11);doc.line(M+13,y+11.5,M+mpW/2-2,y+11.5)
      doc.setFont('helvetica','bold');txt('Banque :',M+mpW/2+2,y+11);doc.setFont('helvetica','normal');txt(f.banque||'',M+mpW/2+16,y+11);doc.line(M+mpW/2+16,y+11.5,M+mpW-4,y+11.5)
      doc.setFont('helvetica','bold');txt('Date d\u2019\u00e9ch\u00e9ance :',M+4,y+16.5);doc.setFont('helvetica','normal');txt(f.dateEcheance||'',M+28,y+16.5);doc.line(M+28,y+17,M+mpW/2-2,y+17)
      doc.setFont('helvetica','bold');txt('R\u00e9f\u00e9rences :',M+mpW/2+2,y+16.5);doc.setFont('helvetica','normal');txt(f.references||'',M+mpW/2+20,y+16.5);doc.line(M+mpW/2+20,y+17,M+mpW-4,y+17);y+=18+5
      fc(...N);doc.rect(M,y,W-M*2,7,'F');tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt('CONDITIONS G\u00c9N\u00c9RALES',W/2,y+4.8,{align:'center'});y+=7
      fc(...WH);doc.rect(M,y,W-M*2,16,'F');dc(...BD);lw(0.3);doc.rect(M,y,W-M*2,16);tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
      txt('Paiement \u00e0 r\u00e9ception de la facture. Retard de paiement : p\u00e9nalit\u00e9s selon le taux BCE + 10 points.',M+4,y+5)
      txt('En cas de litige, juridiction comp\u00e9tente : Tribunal comp\u00e9tent du si\u00e8ge social.',M+4,y+10)
      txt('TVA non applicable, art. 293 B du CGI (association loi 1901).',M+4,y+15);y+=16+5
    }

    // ═══ SIGNATURES (identiques admin) ═══
    const adminSig = f.adminSignature || f.sigEntreprise || null
    const userSig  = f.userSignature  || f.sigClient     || null
    const sigGap=4, sigColW=(W-M*2-sigGap)/2, sigTH=7, sigBH=28
    ;[[(f.entreprise||(isForm?'Entreprise':'Émetteur')).slice(0,28), adminSig, f.adminSignedAt],
      [(f.clientNom||'Client').slice(0,28), userSig, f.userSignedAt]].forEach(([label,sigData,signedAt],i)=>{
      const sx=M+i*(sigColW+sigGap)
      fc(...N);doc.rect(sx,y,sigColW,sigTH,'F')
      tc(...WH);doc.setFontSize(6.5);doc.setFont('helvetica','bold');txt(label,sx+sigColW/2,y+4.8,{align:'center'})
      fc(...WH);doc.rect(sx,y+sigTH,sigColW,sigBH,'F')
      dc(...BD);lw(0.3);doc.rect(sx,y+sigTH,sigColW,sigBH)
      dc(...N);lw(0.4);doc.rect(sx,y,sigColW,sigTH)
      if(sigData){
        try{doc.addImage(sigData,'PNG',sx+4,y+sigTH+3,sigColW-8,sigBH-8)}catch(e){}
        if(signedAt){tc(100,115,140);doc.setFontSize(5.5);doc.setFont('helvetica','normal');txt(new Date(signedAt).toLocaleDateString('fr-FR'),sx+sigColW/2,y+sigTH+sigBH-3,{align:'center'})}
      } else {
        tc(160,170,190);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
        txt('En attente de signature',sx+sigColW/2,y+sigTH+sigBH/2,{align:'center'})
      }
    })
    y+=sigTH+sigBH+3

    // ═══ FOOTER ═══
    if(isForm){
      fc(...G);doc.rect(0,H-22,W,1,'F');fc(...N);doc.rect(0,H-21,W,21,'F')
      tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt("Bon de livraison - Ce document atteste de la remise de l'animal au client",W/2,H-13,{align:'center'})
      tc(160,175,200);doc.setFontSize(6);doc.setFont('helvetica','normal');txt('Document confidentiel - Destinataire uniquement',W/2,H-8,{align:'center'})
      txt('Animaux pour Adoption de la France  |  Association loi 1901',W/2,H-3,{align:'center'})
      doc.save(`bon-de-livraison-${f.clientNom||f.formNumber||Date.now()}.pdf`)
    } else {
      fc(...G);doc.rect(0,H-16,W,1.5,'F');fc(...N);doc.rect(0,H-14.5,W,14.5,'F')
      tc(...WH);doc.setFontSize(6.5);doc.setFont('helvetica','bold');txt('TVA non applicable - article 293 B du Code G\u00e9n\u00e9ral des Imp\u00f4ts',W/2,H-9,{align:'center'})
      tc(160,175,200);doc.setFontSize(5.5);doc.setFont('helvetica','normal');txt('Document comptable - Conservez ce document pour vos archives',W/2,H-5,{align:'center'})
      txt('Animaux pour Adoption de la France  |  Association loi 1901',W/2,H-1.5,{align:'center'})
      doc.save(`facture-${f.clientNom||f.invoiceNumber||Date.now()}.pdf`)
    }
  }

  const ADOPTION_FORMS_SEEN_KEY = 'user_adoption_forms_seen'
  const loadAdoptionForms = async (acknowledge = false) => {
    setAdoptionLoading(true)
    try {
      const data = await adoptionAPI.getMy()
      const forms = data.forms || []
      setAdoptionForms(forms)

      let seen = {}
      try { seen = JSON.parse(localStorage.getItem(ADOPTION_FORMS_SEEN_KEY) || '{}') } catch {}
      const keyOf = (f) => f._id || f.id || ''

      if (acknowledge) {
        const updated = { ...seen }
        forms.forEach(f => { const key = keyOf(f); if (key) updated[key] = true })
        localStorage.setItem(ADOPTION_FORMS_SEEN_KEY, JSON.stringify(updated))
        setAdoptionFormsBadge(0)
      } else {
        const newOnes = forms.filter(f => {
          const key = keyOf(f)
          return key && !seen[key]
        })
        setAdoptionFormsBadge(newOnes.length)
        const changed = forms.some(f => {
          const key = keyOf(f)
          return key && !(key in seen)
        })
        if (changed) {
          const updated = { ...seen }
          forms.forEach(f => { const key = keyOf(f); if (key) updated[key] = false })
          localStorage.setItem(ADOPTION_FORMS_SEEN_KEY, JSON.stringify(updated))
        }
      }
    } catch {} finally {
      setAdoptionLoading(false)
    }
  }

  const handleAdoptionPDF = async (f) => {
    setDownloadingAdoptionId(f._id)
    try {
      // Récupérer le formulaire complet avec la signature (getMy() l'exclut pour alléger)
      let fullForm = f
      if (!f.signatureDataUrl) {
        const data = await adoptionAPI.getMyFormById(f._id)
        fullForm = data.form
      }

      // Charger le logo
      const logoDataUrl = await _getLogoUser()

      const doc = generateAdoptionPDF({
        data: {
          s1: fullForm.s1 || {},
          s2: fullForm.s2 || {},
          s3: fullForm.s3 || {},
          s4: fullForm.s4 || {},
          s5: fullForm.s5 || {},
          s6: fullForm.s6 || {},
        },
        signatureDataUrl: fullForm.signatureDataUrl || null,
        user: fullForm.submittedBy || user,
        formNumber: fullForm.formNumber,
        logoDataUrl,
      })
      doc.save(`${fullForm.formNumber || 'formulaire-adoption'}.pdf`)
    } catch (e) {
      alert('Erreur lors de la génération du PDF.')
    } finally {
      setDownloadingAdoptionId(null)
    }
  }

  const docsBadgeTotal = invoicesBadge + pendingDocsBadge + animalDocBadge + adoptionFormsBadge
  const NAV = [
    { sec:'Mon espace', items:[
      { id:'overview',  icon:<IcoDashboard />, label:'Accueil',       badge: null },
      { id:'orders',    icon:<IcoOrders />,    label:'Mes achats',    badge: ordersBadge   > 0 ? ordersBadge   : null },
      { id:'favorites', icon:<IcoHeart />,     label:'Mes favoris',   badge: favListings.length > 0 ? favListings.length : null },
      { id:'listings',  icon:<IcoListings />,  label:'Mes annonces',  badge: listingNotifBadge > 0 ? listingNotifBadge : null },
      { id:'messages',  icon:<IcoMessages />,  label:'Mes messages',  badge: unreadMsgs    > 0 ? unreadMsgs    : null },
      { id:'invoices',  icon:<IcoInvoice />,   label:'Mes documents',   badge: docsBadgeTotal > 0 ? docsBadgeTotal : null },
      { id:'transport', icon:<IcoTruck />,     label:'Mes transports', badge: tripsNewBadge > 0 ? tripsNewBadge : null },
    ]},
    { sec:'Compte', items:[
      { id:'profile',  icon:<IcoUser />,     label:'Mon profil',  badge: null },
      { id:'settings', icon:<IcoSettings />, label:'Paramètres',  badge: null },
    ]},
  ]

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16, color:'var(--slate)' }}>
      <div className="catalog-spinner" />
      <p>Chargement de votre espace…</p>
    </div>
  )

  return (
    <div className="ud-layout">

      {/* ── SIDEBAR ── */}
      <aside className="ud-sb">
        <div className="ud-sb-logo">
          <div className="ud-sb-logo-icon">
            <img src={require('../../assets/monlogo.jpeg')} alt="Logo" className="ud-sb-logo-img" onError={(e)=>{e.target.style.display='none'}} />
          </div>
          <div className="ud-sb-logo-txt">Animaux pour Adoption <span className="ud-sb-logo-sub">Mon espace</span></div>
        </div>

        <nav className="ud-nav">
          {NAV.map(sec => (
            <div key={sec.sec}>
              <div className="ud-nav-sec">{sec.sec}</div>
              {sec.items.map(item => (
                <button
                  key={item.id}
                  className={`ud-nav-item${activeView===item.id?' on':''}`}
                  onClick={() => handleNavClick(item.id)}
                >
                  <span className="ud-nav-icon">{item.icon}</span>
                  {item.label}
                  {item.badge && <span className="ud-nav-badge">{item.badge}</span>}
                </button>
              ))}
              <div className="ud-divider" />
            </div>
          ))}
        </nav>

        <div className="ud-sb-user">
          <div className="ud-sb-avatar">{initials}</div>
          <div>
            <div className="ud-sb-uname">{user.name || 'Utilisateur'}</div>
            <div className="ud-sb-uemail">{user.email}</div>
          </div>
          <button className="ud-sb-logout" title="Déconnexion" onClick={onLogout}><IcoLogout /></button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div className="ud-main">

        {/* Topbar */}
        <div className="ud-topbar">
          {/* Hamburger — mobile uniquement */}
          <button className="ud-hamburger" onClick={() => setMenuOpen(true)} aria-label="Menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            {(ordersBadge + unreadMsgs + invoicesBadge + listingNotifBadge + tripsNewBadge) > 0 && <span className="ud-hamburger-dot" />}
          </button>
          {/* Bouton retour — desktop uniquement */}
          <button className="ud-back-to-site" onClick={onBack}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Retour au site
          </button>
          <div className="ud-topbar-title">{PAGE_TITLES[activeView]}</div>
          <div className="ud-topbar-greet">Bonjour, {user.name?.split(' ')[0] || 'vous'}</div>
        </div>

        {/* Drawer mobile — tous les boutons */}
        {menuOpen && (
          <div className="ud-drawer-overlay" onClick={() => setMenuOpen(false)}>
            <div className="ud-drawer" onClick={e => e.stopPropagation()}>

              {/* En-tête : bienvenue */}
              <div className="ud-drawer-head">
                <div className="ud-drawer-user">
                  <div className="ud-sb-avatar">{initials}</div>
                  <div>
                    <div className="ud-sb-uname">Bonjour, {user.name?.split(' ')[0] || 'vous'} 👋</div>
                    <div className="ud-sb-uemail">{user.email}</div>
                  </div>
                </div>
                <button className="ud-drawer-close" onClick={() => setMenuOpen(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>

              {/* Navigation complète */}
              <div className="ud-drawer-body">
                {[
                  { id:'overview',  icon:<IcoDashboard />, label:'Accueil',        badge: null },
                  { id:'orders',    icon:<IcoOrders />,    label:'Mes achats',     badge: ordersBadge        > 0 ? ordersBadge        : null },
                  { id:'listings',  icon:<IcoListings />,  label:'Mes annonces',   badge: listingNotifBadge  > 0 ? listingNotifBadge  : null },
                  { id:'messages',  icon:<IcoMessages />,  label:'Mes messages',   badge: unreadMsgs         > 0 ? unreadMsgs         : null },
                  { id:'invoices',  icon:<IcoInvoice />,   label:'Mes documents',  badge: docsBadgeTotal > 0 ? docsBadgeTotal : null },
                  { id:'favorites', icon:<IcoHeart />,     label:'Mes favoris',    badge: null },
                  { id:'transport', icon:<IcoTruck />,     label:'Mes transports', badge: tripsNewBadge      > 0 ? tripsNewBadge      : null },
                  { id:'profile',   icon:<IcoUser />,      label:'Mon profil',     badge: null },
                  { id:'settings',  icon:<IcoSettings />,  label:'Paramètres',     badge: null },
                ].map(item => (
                  <button
                    key={item.id}
                    className={`ud-drawer-item${activeView === item.id ? ' active' : ''}`}
                    onClick={() => { handleNavClick(item.id); setMenuOpen(false) }}
                  >
                    <span className="ud-drawer-icon">{item.icon}</span>
                    <span className="ud-drawer-label">{item.label}</span>
                    {item.badge && <span className="ud-nav-badge">{item.badge > 9 ? '9+' : item.badge}</span>}
                  </button>
                ))}
              </div>

              {/* Pied : retour au site + déconnexion */}
              <div className="ud-drawer-footer">
                <button className="ud-drawer-back" onClick={() => { setMenuOpen(false); onBack() }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Retour au site
                </button>
                <button className="ud-drawer-logout" onClick={onLogout}>
                  <IcoLogout /> Quitter
                </button>
              </div>

            </div>
          </div>
        )}

        <div className="ud-content">

          {/* ══ ACCUEIL ══ */}
          {activeView === 'overview' && (
            <>
              <div className="ud-kpi-row">
                <div className="ud-kpi"><div className="ud-kpi-label">Animaux achetés</div><div className="ud-kpi-val">{orders.length}</div><div className="ud-kpi-sub">Total</div></div>
                <div className="ud-kpi"><div className="ud-kpi-label">Favoris</div><div className="ud-kpi-val">{favListings.length}</div><div className="ud-kpi-sub">Sauvegardés</div></div>
                <div className="ud-kpi"><div className="ud-kpi-label">Messages non lus</div><div className="ud-kpi-val">{unreadMsgs}</div><div className="ud-kpi-sub">À consulter</div></div>
                <div className="ud-kpi"><div className="ud-kpi-label">Nouvelles factures</div><div className="ud-kpi-val">{newInvoices}</div><div className="ud-kpi-sub">À télécharger</div></div>
              </div>

              {/* Derniers achats — scroll */}
              <div className="ud-card">
                <div className="ud-card-head">
                  <div className="ud-card-title"><IcoOrders /> Derniers achats</div>
                  <span className="ud-card-count">{orders.length}</span>
                </div>
                {orders.length === 0 ? (
                  <div className="ud-empty"><div className="ud-empty-icon"><IcoOrders /></div><h3>Aucun achat pour l'instant</h3><p>Vos commandes apparaîtront ici.</p></div>
                ) : (
                  <div className="ud-scroll-list">
                    {orders.map(o => (
                      <div key={o.orderId} className="ud-animal-row">
                        <img className="ud-animal-img" src={o.img} alt={o.title} />
                        <div className="ud-animal-info">
                          <div className="ud-animal-title">{o.title}</div>
                          <div className="ud-animal-meta">{o.breed} · {o.city}</div>
                          <div className="ud-animal-price">{fmt(o.price)}</div>
                        </div>
                        {o.paymentStatus === 'paid'
                          ? <span className="ud-status ud-st-ok"><IcoCheck /> Confirmé</span>
                          : <span className="ud-status ud-st-wait"><IcoWait /> En attente paiement</span>
                        }
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mes favoris — scroll */}
              <div className="ud-card">
                <div className="ud-card-head">
                  <div className="ud-card-title"><IcoHeart /> Mes favoris</div>
                  <span className="ud-card-count">{favListings.length}</span>
                </div>
                {favListings.length === 0 ? (
                  <div className="ud-empty"><div className="ud-empty-icon"><IcoHeart /></div><h3>Aucun favori sauvegardé</h3><p>Ajoutez des annonces en favoris pour les retrouver ici.</p></div>
                ) : (
                  <div className="ud-scroll-list">
                    {favListings.map(l => (
                      <div key={l.id} className="ud-animal-row">
                        <img className="ud-animal-img" src={l.img} alt={l.title} />
                        <div className="ud-animal-info" onClick={() => onDetailClick(l.id)} style={{cursor:'pointer'}}>
                          <div className="ud-animal-title">{l.title}</div>
                          <div className="ud-animal-meta">{l.breed} · {l.city}</div>
                          <div className="ud-animal-price">{fmt(l.price)}</div>
                        </div>
                        <div className="ud-row-actions">
                          <button className="ud-action-btn" onClick={() => onDetailClick(l.id)}><IcoEye /></button>
                          <button className="ud-action-btn ud-action-del" onClick={() => handleToggleFav(l.id)}><IcoTrash /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══ MES ACHATS ══ */}
          {activeView === 'orders' && (() => {
            const PER = 10
            const totalPg = Math.max(1, Math.ceil(orders.length / PER))
            const paginated = orders.slice((ordersPage-1)*PER, ordersPage*PER)
            return (
              <div className="ud-card">
                <div className="ud-card-head">
                  <div className="ud-card-title"><IcoOrders /> Mes achats</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {newOrders > 0 && (
                      <button className="ud-edit-btn" onClick={markAllOrdersRead}>
                        <IcoCheck /> Tout marquer lu
                      </button>
                    )}
                  </div>
                </div>
                {orders.length === 0 ? (
                  <div className="ud-empty">
                    <div className="ud-empty-icon"><IcoOrders /></div>
                    <h3>Aucun achat pour l'instant</h3>
                    <p>Parcourez nos annonces et adoptez votre premier animal.</p>
                    <button className="ud-empty-btn" onClick={onBack}>Parcourir les annonces</button>
                  </div>
                ) : (
                  <>
                    <div className="ud-orders-table-wrap">
                      <table className="ud-orders-table">
                        <thead>
                          <tr>
                            <th>Animal</th>
                            <th>Réf. commande</th>
                            <th>Date</th>
                            <th>Montant</th>
                            <th>Paiement</th>
                            <th>Statut paiement</th>
                            <th>Livraison</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginated.map(o => (
                            <tr
                              key={o.orderId}
                              className={o.isNew ? 'ud-tr-new' : ''}
                              onClick={() => { if (o.isNew) handleOrderRead(o.orderId) }}
                              style={{ cursor: o.isNew ? 'pointer' : 'default' }}
                            >
                              <td>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <img src={o.img} alt={o.title} className="ud-tbl-thumb" />
                                  <div>
                                    {o.isNew && <span className="ud-inline-new">Nouveau</span>}
                                    <div className="ud-tbl-name">{o.title}</div>
                                    <div className="ud-tbl-sub">{o.breed}</div>
                                  </div>
                                </div>
                              </td>
                              <td><span className="ud-tbl-ref">{o.orderNumber || o.orderId}</span></td>
                              <td style={{ fontSize:12, color:'var(--slate)', whiteSpace:'nowrap' }}>{o.orderDate}</td>
                              <td><strong style={{ color:'var(--gold-dk)' }}>{fmt(o.price)}</strong></td>
                              <td>
                                <span className="ud-pay-method">
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                                      {fmtPay(o.paymentMethod, o.cardBrand, o.cardLast4)}
                                    </span>
                              </td>
                              <td>
                                {o.paymentStatus === 'paid'
                                  ? <span className="ud-status ud-st-ok"><IcoCheck /> Payé</span>
                                  : o.paymentStatus === 'pending_wire'
                                    ? <span className="ud-status ud-st-wait"><IcoWait /> En attente</span>
                                    : <span className="ud-status ud-st-wait"><IcoWait /> {o.paymentStatus || 'En attente'}</span>
                                }
                              </td>
                              <td>
                                <span className={`ud-status ${o.deliveryStatus === 'delivered' ? 'ud-st-ok' : o.deliveryStatus === 'shipped' ? 'ud-st-ship' : 'ud-st-wait'}`}>
                                  {o.deliveryStatus === 'delivered' ? <><IcoCheck /> Livré</> : o.deliveryStatus === 'shipped' ? <><IcoTruck /> Expédié</> : <><IcoWait /> En attente</>}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalPg > 1 && (
                      <div className="ud-pagination">
                        <button className="ud-pg-btn" disabled={ordersPage===1} onClick={() => setOrdersPage(p=>p-1)}>←</button>
                        {Array.from({length:totalPg},(_,i)=>i+1).map(p => (
                          <button key={p} className={`ud-pg-btn${ordersPage===p?' active':''}`} onClick={() => setOrdersPage(p)}>{p}</button>
                        ))}
                        <button className="ud-pg-btn" disabled={ordersPage===totalPg} onClick={() => setOrdersPage(p=>p+1)}>→</button>
                        <span className="ud-pg-info">{(ordersPage-1)*PER+1}–{Math.min(ordersPage*PER, orders.length)} / {orders.length}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })()}

          {/* ══ MES FAVORIS ══ */}
          {activeView === 'favorites' && (() => {
            const PER = 9
            const totalPg = Math.max(1, Math.ceil(favListings.length / PER))
            const paginated = favListings.slice((favsPage-1)*PER, favsPage*PER)
            return (
              <div>
                <div className="ud-fav-header">
                  <div className="ud-card-title" style={{fontSize:18}}><IcoHeart /> Mes favoris</div>
                  <span className="ud-card-count">{favListings.length} annonce{favListings.length!==1?'s':''}</span>
                </div>
                {favListings.length === 0 ? (
                  <div className="ud-card">
                    <div className="ud-empty"><div className="ud-empty-icon"><IcoHeart /></div><h3>Aucun favori sauvegardé</h3><p>Ajoutez des annonces en favoris pour les retrouver ici.</p><button className="ud-empty-btn" onClick={onBack}>Parcourir les annonces</button></div>
                  </div>
                ) : (
                  <>
                    <div className="ud-fav-grid">
                      {paginated.map(l => (
                        <ListingCard
                          key={l.id}
                          listing={l}
                          isFav={true}
                          onFav={() => handleToggleFav(l.id)}
                          onDetail={() => onDetailClick(l.id)}
                          onAddCart={() => {}}
                        />
                      ))}
                    </div>
                    {totalPg > 1 && (
                      <div className="ud-pagination" style={{marginTop:16}}>
                        <button className="ud-pg-btn" disabled={favsPage===1} onClick={() => setFavsPage(p=>p-1)}>←</button>
                        {Array.from({length:totalPg},(_,i)=>i+1).map(p => (
                          <button key={p} className={`ud-pg-btn${favsPage===p?' active':''}`} onClick={() => setFavsPage(p)}>{p}</button>
                        ))}
                        <button className="ud-pg-btn" disabled={favsPage===totalPg} onClick={() => setFavsPage(p=>p+1)}>→</button>
                        <span className="ud-pg-info">{(favsPage-1)*PER+1}–{Math.min(favsPage*PER, favListings.length)} / {favListings.length}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })()}

          {/* ══ MES ANNONCES ══ */}
          {activeView === 'listings' && (
            <div className="ud-card">
              <div className="ud-card-head">
                <div className="ud-card-title"><IcoListings /> Mes annonces</div>
                {!showListingForm && (
                  <button className="ud-edit-btn" onClick={openNewListingForm}><IcoPlus /> Publier une annonce</button>
                )}
              </div>

              {listingMsg && <div className="ud-listing-msg">{listingMsg}</div>}

              {showListingForm ? (
                <>
                  <div className="ud-form-grid">
                    <div className="ud-fg ud-fg-full">
                      <label className="ud-label">Photos (max 10)</label>
                      <div
                        className={`ud-upload${lDrag ? ' drag' : ''}`}
                        onClick={() => lFileRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); setLDrag(true) }}
                        onDragLeave={() => setLDrag(false)}
                        onDrop={e => { e.preventDefault(); setLDrag(false); handleListingFiles(e.dataTransfer.files) }}
                      >
                        <IcoUpload />
                        <div className="ud-upload-title">Glissez vos photos ici ou cliquez</div>
                        <div className="ud-upload-sub">JPG, PNG, WebP — 10 photos maximum</div>
                        <input
                          ref={lFileRef}
                          type="file"
                          multiple
                          accept="image/png, image/jpeg, image/webp"
                          style={{ display:'none' }}
                          onChange={e => { handleListingFiles(e.target.files); e.target.value = '' }}
                        />
                      </div>
                      {lCompressing && <div className="ud-upload-sub" style={{ marginTop:6 }}>Compression des photos…</div>}
                      {lImgs.length > 0 && (
                        <div className="ud-img-preview">
                          {lImgs.map((img, i) => (
                            <div key={i} className="ud-img-item">
                              <img src={img.url} alt="" />
                              <button className="ud-img-del" onClick={() => removeListingImageAt(i)} type="button"><IcoX /></button>
                              {i === 0 && <div className="ud-img-main">Principale</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {lImgError && <div className="ud-listing-msg">{lImgError}</div>}
                    </div>

                    <div className="ud-fg ud-fg-full">
                      <label className="ud-label">Titre *</label>
                      <input className="ud-input" placeholder="Ex: Chiot Golden Retriever, 2 mois" value={lTitle} onChange={e => setLTitle(e.target.value)} />
                    </div>

                    <div className="ud-fg">
                      <label className="ud-label">Espèce *</label>
                      <BreedSelect options={CATEGORIES} value={lCat} onChange={v => { setLCat(v); setLBreed('') }} placeholder="Sélectionner une espèce…" />
                    </div>
                    <div className="ud-fg">
                      <label className="ud-label">Race *</label>
                      <BreedSelect category={lCat} value={lBreed} onChange={setLBreed} placeholder="Sélectionner ou taper une race…" />
                    </div>

                    <div className="ud-fg">
                      <label className="ud-label">
                        Prix (€) {lSellerType !== 'Don' && '*'}
                        {lSellerType === 'Don' && <span style={{marginLeft:6, fontSize:11, fontWeight:500, color:'var(--green-dk)'}}>Don gratuit</span>}
                      </label>
                      <input
                        className="ud-input"
                        type="number"
                        min="0"
                        placeholder="850"
                        value={lSellerType === 'Don' ? '0' : lPrice}
                        onChange={e => { if (lSellerType !== 'Don') setLPrice(e.target.value) }}
                        disabled={lSellerType === 'Don'}
                        style={lSellerType === 'Don' ? {background:'var(--bg2)', color:'var(--slate-lt)', cursor:'not-allowed'} : {}}
                      />
                    </div>
                    <div className="ud-fg">
                      <label className="ud-label">Âge</label>
                      <input className="ud-input" placeholder="2 mois" value={lAge} onChange={e => setLAge(e.target.value)} />
                    </div>

                    <div className="ud-fg">
                      <label className="ud-label">Sexe</label>
                      <BreedSelect options={['Mâle','Femelle']} value={lSex} onChange={setLSex} placeholder="Sélectionner…" />
                    </div>
                    <div className="ud-fg">
                      <label className="ud-label">Livraison</label>
                      <BreedSelect options={DELIVERY_OPTIONS} value={lDelivery} onChange={setLDelivery} placeholder="Sélectionner…" />
                    </div>

                    <div className="ud-fg">
                      <label className="ud-label">Ville *</label>
                      <input className="ud-input" placeholder="Paris" value={lCity} onChange={e => setLCity(e.target.value)} />
                    </div>
                    <div className="ud-fg">
                      <label className="ud-label">Région</label>
                      <BreedSelect options={REGIONS} value={lRegion} onChange={setLRegion} placeholder="Sélectionner ou taper une région…" />
                    </div>

                    <div className="ud-fg">
                      <label className="ud-label">Type de vendeur</label>
                      <BreedSelect
                        options={['Particulier','Éleveur','Don']}
                        value={lSellerType === 'Part' ? 'Particulier' : lSellerType === 'Pro' ? 'Éleveur' : 'Don'}
                        onChange={v => {
                          const norm = v === 'Particulier' ? 'Part' : v === 'Éleveur' ? 'Pro' : 'Don'
                          setLSellerType(norm)
                          if (norm === 'Don') setLPrice('0')
                        }}
                        placeholder="Sélectionner le type…"
                      />
                    </div>
                    <div className="ud-fg">
                      <label className="ud-label">Nom à afficher</label>
                      <input className="ud-input" placeholder={fName || 'Votre nom'} value={lSeller} onChange={e => setLSeller(e.target.value)} />
                    </div>

                    <div className="ud-fg ud-fg-full">
                      <label className="ud-label">Téléphone de contact</label>
                      <input className="ud-input" type="tel" placeholder={fPhone || '+33 6 00 00 00 00'} value={lPhone} onChange={e => setLPhone(e.target.value)} />
                    </div>

                    <div className="ud-fg ud-fg-full">
                      <label className="ud-label">
                        Description * <span style={{ textTransform:'none', fontWeight:400, marginLeft:6, color: lDesc.length > 0 && lDesc.length < 10 ? 'var(--red)' : 'var(--slate-lt)' }}>{lDesc.length}/1000</span>
                      </label>
                      <textarea
                        className="ud-textarea"
                        rows={5}
                        maxLength={1010}
                        placeholder="Décrivez l'animal : caractère, santé, documents fournis… (10 à 1000 caractères)"
                        value={lDesc}
                        onChange={e => setLDesc(e.target.value)}
                      />
                    </div>

                    <div className="ud-fg ud-fg-full">
                      <div className="ud-listing-cbx-row">
                        <label className="ud-cbx"><input type="checkbox" checked={lVacc} onChange={e => setLVacc(e.target.checked)} /> Vacciné</label>
                        <label className="ud-cbx"><input type="checkbox" checked={lMicro} onChange={e => setLMicro(e.target.checked)} /> Pucé</label>
                        <label className="ud-cbx"><input type="checkbox" checked={lPedi} onChange={e => setLPedi(e.target.checked)} /> Pédigrée</label>
                      </div>
                    </div>
                  </div>

                  <div className="ud-listing-notice">
                    <IcoWait />
                    {editingListing
                      ? "Votre modification sera soumise à un nouvel examen avant d'être republiée."
                      : "Votre annonce sera examinée par notre équipe avant d'apparaître sur le site."}
                  </div>

                  <div className="ud-form-actions">
                    <button className="ud-save-btn" onClick={saveListingForm} disabled={listingSaving}>
                      {listingSaving ? 'Envoi en cours…' : editingListing ? <><IcoSave /> Enregistrer</> : <><IcoPlus /> Publier l'annonce</>}
                    </button>
                    <button className="ud-cancel-btn" disabled={listingSaving} onClick={() => { setShowListingForm(false); resetListingForm() }}>Annuler</button>
                  </div>
                </>
              ) : listingsLoading ? (
                <div className="ud-empty"><div className="catalog-spinner" /><p>Chargement de vos annonces…</p></div>
              ) : myListings.length === 0 ? (
                <div className="ud-empty">
                  <div className="ud-empty-icon"><IcoListings /></div>
                  <h3>Aucune annonce publiée</h3>
                  <div style={{fontSize:13,color:'var(--slate)',maxWidth:340,margin:'0 auto',lineHeight:1.6}}>Publiez votre première annonce en cliquant sur le bouton <strong style={{color:'var(--blue)'}}>+ Publier une annonce</strong> en haut à droite pour trouvez un foyer pour votre animal.</div>                  
                </div>
              ) : (
                <div className="ud-mylistings-grid">
                  {myListings.map(l => (
                    <div key={l._id} className="ud-mylisting-card">
                      <div className="ud-mylisting-img-wrap">
                        {l.images?.[0]?.url
                          ? <img src={l.images[0].url} alt={l.title} />
                          : <div className="ud-mylisting-noimg"><IcoListings /></div>}
                        <span className={`ud-mylisting-status status-${l.status}`}>
                          {l.status === 'active' ? 'En ligne' : l.status === 'pending' ? "En attente d'examen" : l.status === 'rejected' ? 'Rejetée' : 'Vendue'}
                        </span>
                      </div>
                      <div className="ud-mylisting-body">
                        <div className="ud-mylisting-breed">{l.breed}</div>
                        <div className="ud-mylisting-title">{l.title}</div>
                        <div className="ud-mylisting-price">{fmt(l.price)}</div>
                        {l.status === 'rejected' && (
                          <div className="ud-mylisting-reason">
                            <IcoBan /> {l.rejectionReason ? l.rejectionReason : 'Aucun motif renseigné par l’équipe.'}
                          </div>
                        )}
                        <div className="ud-mylisting-meta">
                          <IcoEye /> {l.views || 0} vue{(l.views || 0) > 1 ? 's' : ''} · {new Date(l.createdAt).toLocaleDateString('fr-FR')}
                        </div>
                        <div className="ud-mylisting-actions">
                          {l.status !== 'sold' && (
                            <button className="ud-action-btn" onClick={() => openEditListingForm(l)}><IcoEdit /> Modifier</button>
                          )}
                          <button className="ud-action-btn ud-action-del" onClick={() => setConfirmDelListing({ id: l._id, title: l.title })}><IcoTrash /> Supprimer</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══ MES MESSAGES ══ */}
          {activeView === 'messages' && (() => {
            const selectedConv = conversations.find(c => c.key === selectedConvKey)

            // Conversation toute nouvelle, ouverte via "Contacter l'annonceur" avant
            // l'envoi du tout premier message — pas encore présente dans la liste
            const contactKey    = contactListing ? (contactListing.id || contactListing._id) : null
            const isPendingNew  = selectedConvKey && !selectedConv && selectedConvKey === contactKey

            // Barre annonce : celle de la conversation sélectionnée, ou celle de
            // contactListing si la conversation est toute nouvelle (pas encore en liste)
            const bannerSource = selectedConv?.listing || (isPendingNew ? contactListing : null)
            const chatBanner = bannerSource ? {
              img:   bannerSource.img || bannerSource.images?.[0]?.url,
              breed: bannerSource.breed,
              title: bannerSource.title,
              price: bannerSource.price,
            } : null

            const convTitle = (c) => c.listing ? c.listing.title : 'Questions générales'
            const convBreed = (c) => c.listing ? c.listing.breed : 'Support'
            const convImg   = (c) => c.listing?.images?.[0]?.url || c.img
            const fmtConvDate = (iso) => {
              if (!iso) return ''
              try { return new Date(iso).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit' }) } catch { return '' }
            }

            return (
              <div className="ud-card ud-msg-card">
                <div className="ud-card-head">
                  <div className="ud-card-title"><IcoMessages /> Mes messages</div>
                  <span className="ud-card-count">{conversations.length}</span>
                </div>

                <div className="ud-msg-split">

                  {/* ── Liste des conversations, une par annonce ── */}
                  <div className="ud-conv-list">
                    {conversations.length === 0 && !isPendingNew ? (
                      <div className="ud-conv-list-empty">
                        <IcoMessages />
                        <p>Aucune conversation pour l'instant.</p>
                        <p style={{ fontSize:11.5, color:'var(--slate-lt)' }}>Contactez un annonceur depuis une fiche annonce pour démarrer une discussion.</p>
                      </div>
                    ) : (
                      <>
                        {isPendingNew && (
                          <div className="ud-conv-item selected">
                            {contactListing.img || contactListing.images?.[0]?.url ? (
                              <img className="ud-conv-item-img" src={contactListing.img || contactListing.images?.[0]?.url} alt="" />
                            ) : (
                              <div className="ud-conv-item-icon"><IcoMessages /></div>
                            )}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div className="ud-conv-breed">{convBreed({ listing: contactListing })}</div>
                              <div className="ud-conv-row"><span className="ud-conv-name">{contactListing.title}</span></div>
                              <div className="ud-conv-row"><span className="ud-conv-preview">Nouvelle conversation</span></div>
                            </div>
                          </div>
                        )}
                        {conversations.map(c => (
                          <div
                            key={c.key}
                            className={`ud-conv-item${selectedConvKey === c.key ? ' selected' : ''}`}
                            onClick={() => openConversation(c.key)}
                          >
                            {convImg(c) ? (
                              <img className="ud-conv-item-img" src={convImg(c)} alt="" />
                            ) : (
                              <div className="ud-conv-item-icon"><IcoMessages /></div>
                            )}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div className="ud-conv-breed">{convBreed(c)}</div>
                              <div className="ud-conv-row">
                                <span className={`ud-conv-name${c.unreadCount > 0 ? ' unread' : ''}`}>{convTitle(c)}</span>
                                <span className="ud-conv-date">{fmtConvDate(c.lastDate)}</span>
                              </div>
                              <div className="ud-conv-row">
                                <span className="ud-conv-preview">{c.lastMessage}</span>
                                {c.unreadCount > 0 && <span className="ud-conv-badge">{c.unreadCount}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* ── Fil de discussion de la conversation sélectionnée ── */}
                  <div className="ud-conv-thread">
                    {selectedConvKey ? (
                      <ChatThread
                        key={selectedConvKey}
                        messages={threadMsgs}
                        loading={threadLoading}
                        isMine={(m) => m.sender?.role !== 'admin'}
                        onSend={sendChatMessage}
                        sending={chatSending}
                        placeholder="Écrivez à l'équipe Animaux pour Adoption…"
                        emptyTitle="Aucun message pour l'instant"
                        emptyHint={selectedConvKey === 'general'
                          ? "Posez vos questions générales — l'équipe vous répond rapidement."
                          : "Posez vos questions sur cette annonce — l'équipe vous répond rapidement."}
                        inputDefault={isPendingNew ? `Bonjour, je suis intéressé(e) par « ${contactListing.title} ».` : ''}
                        banner={chatBanner}
                        onDeleteMessage={handleDeleteMessage}
                        onDeleteConversation={selectedConv ? handleDeleteConversation : null}
                        peerStatus={adminStatus}
                      />
                    ) : (
                      <div className="ud-conv-empty">
                        <IcoMessages />
                        <span>Sélectionnez une conversation</span>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )
          })()}


          {/* ══ MES FACTURES & FORMULAIRES D'ADOPTION ══ */}
          {activeView === 'invoices' && (
            showAdoptionForm ? (
              <AdoptionFormPage
                user={user}
                adoptionAPI={adoptionAPI}
                onBack={() => { setShowAdoptionForm(false); loadAdoptionForms() }}
              />
            ) : (
              <div className="ud-card">
                <div className="ud-card-head">
                  <div className="ud-card-title"><IcoInvoice /> Documents</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {invoiceTab === 'invoices' && userInvoices.filter(i => i.isNew).length > 0 && (
                      <button className="ud-edit-btn" onClick={() => handleInvoiceRead('all')}>
                        <IcoCheck /> Tout marquer lu
                      </button>
                    )}
                    {invoiceTab === 'adoption' && (
                      <button className="ud-edit-btn" onClick={() => setShowAdoptionForm(true)}>
                        + Nouveau formulaire
                      </button>
                    )}
                  </div>
                </div>

                {/* Onglets */}
                <div className="ud-doc-tabs">
                  <button className={`ud-doc-tab${invoiceTab==='invoices'?' active':''}`} onClick={()=>{setInvoiceTab('invoices');setInvoicesPage(1);loadAll()}}>
                    <IcoInvoice /> Mes factures
                    {invoicesBadge>0&&<span className="ud-doc-tab-badge">{invoicesBadge}</span>}
                  </button>
                  <button className={`ud-doc-tab${invoiceTab==='adoption'?' active':''}`} onClick={()=>{setInvoiceTab('adoption');loadAdoptionForms(true)}}>
                    📋 Formulaires d'adoption
                    {adoptionFormsBadge>0&&<span className="ud-doc-tab-badge">{adoptionFormsBadge}</span>}
                  </button>
                  <button className={`ud-doc-tab${invoiceTab==='a-signer'?' active':''}`} onClick={()=>{setInvoiceTab('a-signer');setViewingDoc(null);setSigningDoc(null);loadPendingDocs(true)}}>
                    ✍️ Livraisons &amp; Factures
                    {pendingDocsBadge>0&&<span className="ud-doc-tab-badge" style={{background:'var(--red-lt)',color:'var(--red)'}}>{pendingDocsBadge}</span>}
                  </button>
                  <button className={`ud-doc-tab${invoiceTab==='animal-docs'?' active':''}`} onClick={()=>{setInvoiceTab('animal-docs');setAnimalDocBadge(0);setViewingAnimalDoc(null);setSigningAnimalDoc(null);setAnimalDocsLoading(true);animalDocsAPI.getMyDocs().then(r=>{setAnimalDocs(r.docs||[])}).catch(()=>{}).finally(()=>setAnimalDocsLoading(false))}}>
                    🏥 Docs santé
                    {animalDocBadge>0&&<span className="ud-doc-tab-badge" style={{background:'#FEE2E2',color:'#DC2626'}}>{animalDocBadge}</span>}
                  </button>
                </div>

                {/* ── Onglet Factures ── */}
                {invoiceTab === 'invoices' && (() => {
                  const PER     = 10
                  const totalPg = Math.max(1, Math.ceil(userInvoices.length / PER))
                  const paginated = userInvoices.slice((invoicesPage-1)*PER, invoicesPage*PER)
                  return userInvoices.length === 0 ? (
                    <div className="ud-empty">
                      <div className="ud-empty-icon"><IcoInvoice /></div>
                      <h3>Aucune facture pour l'instant</h3>
                      <p>Vos factures d'achat apparaîtront ici après chaque commande.</p>
                    </div>
                  ) : (
                    <>
                      <div className="ud-orders-table-wrap">
                        <table className="ud-orders-table">
                          <thead>
                            <tr>
                              <th>Référence</th><th>Animal</th><th>Catégorie</th>
                              <th>Paiement</th><th>Montant</th><th>Statut</th><th>Date</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginated.map(inv => (
                              <tr key={inv.id} className={inv.isNew ? 'ud-tr-new' : ''} onClick={() => { if (inv.isNew) handleInvoiceRead(inv.id) }} style={{ cursor: inv.isNew ? 'pointer' : 'default' }}>
                                <td>
                                  <span className="ud-tbl-ref">
                                    {inv.ref}
                                    {inv.isNew && <span className="ud-inv-new-dot" style={{ marginLeft:6 }} />}
                                  </span>
                                </td>
                                <td><div className="ud-tbl-name">{inv.animal}</div></td>
                                <td style={{ fontSize:12, color:'var(--slate)' }}>{inv.category || '—'}</td>
                                <td>
                                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, color:'var(--slate)' }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                                    {fmtPay(inv.paymentMethod, inv.cardBrand, inv.cardLast4)}
                                  </span>
                                </td>
                                <td><strong style={{ color:'var(--gold-dk)' }}>{fmt(inv.amount)}</strong></td>
                                <td>
                                  <span className={`ud-status ${inv.status === 'paid' ? 'ud-st-ok' : 'ud-st-wait'}`}>
                                    {inv.status === 'paid' ? <><IcoCheck /> Payé</> : inv.status === 'refunded' ? 'Remboursé' : 'Annulé'}
                                  </span>
                                </td>
                                <td style={{ fontSize:11, color:'var(--slate-lt)', whiteSpace:'nowrap' }}>{inv.date}</td>
                                <td onClick={e => e.stopPropagation()}>
                                  <button
                                    className="ud-dl-btn"
                                    disabled={downloadingInvId === inv.id}
                                    onClick={() => handleDownloadPDF(inv)}
                                  >
                                    {downloadingInvId === inv.id ? '…' : <><IcoDownload /> PDF</>}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {totalPg > 1 && (
                        <div className="ud-pagination">
                          <button className="ud-pg-btn" disabled={invoicesPage===1} onClick={() => setInvoicesPage(p=>p-1)}>←</button>
                          {Array.from({length:totalPg},(_,i)=>i+1).map(p => (
                            <button key={p} className={`ud-pg-btn${invoicesPage===p?' active':''}`} onClick={() => setInvoicesPage(p)}>{p}</button>
                          ))}
                          <button className="ud-pg-btn" disabled={invoicesPage===totalPg} onClick={() => setInvoicesPage(p=>p+1)}>→</button>
                          <span className="ud-pg-info">{(invoicesPage-1)*PER+1}–{Math.min(invoicesPage*PER, userInvoices.length)} / {userInvoices.length}</span>
                        </div>
                      )}
                    </>
                  )
                })()}

                {/* ── Onglet Formulaires d'adoption ── */}
                {invoiceTab === 'adoption' && (
                  adoptionLoading ? (
                    <div className="ud-empty"><div className="catalog-spinner" /><p>Chargement…</p></div>
                  ) : adoptionForms.length === 0 ? (
                    <div className="ud-empty">
                      <div className="ud-empty-icon"><IcoShield /></div>
                      <h3>Aucun formulaire soumis</h3>
                      <div style={{fontSize:13,color:'var(--slate)',maxWidth:340,margin:'0 auto',lineHeight:1.6}}>Remplissez votre formulaire d'adoption pour démarrer le processus en cliquent sur <strong style={{color:'var(--blue)'}}>+ Nouveau formulaire</strong> en haut à droite.</div>
                    </div>
                  ) : (
                    <div className="ud-adoption-list">
                      {adoptionForms.map(f => (
                        <div key={f._id} className="ud-adoption-row">
                          <div className="ud-adoption-icon">📋</div>
                          <div className="ud-adoption-info">
                            <div className="ud-adoption-num">{f.formNumber}</div>
                            <div className="ud-adoption-meta">
                              Soumis le {new Date(f.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })}
                            </div>
                            {f.s1?.nomPrenom && <div className="ud-adoption-sub">Au nom de : {f.s1.nomPrenom}</div>}
                          </div>
                          <span className="ud-adoption-badge">Soumis ✓</span>
                          <button
                            className="ud-dl-btn"
                            disabled={downloadingAdoptionId === f._id}
                            onClick={() => handleAdoptionPDF(f)}
                          >
                            {downloadingAdoptionId === f._id
                              ? '…'
                              : <><IcoDownload /> PDF</>
                            }
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            )
          )}

          {/* ══ LIVRAISONS & FACTURES (onglet a-signer) ══ */}
          {activeView==='invoices'&&invoiceTab==='a-signer'&&(
            <div className="ud-card">
              {signingDoc ? (
                /* ─── Écran signature ─── */
                <div style={{padding:'20px 18px'}}>
                  <button style={{background:'none',border:'none',color:'var(--slate)',cursor:'pointer',fontSize:13,marginBottom:14,fontFamily:"'Inter',sans-serif",padding:0}} onClick={()=>setSigningDoc(null)}>← Retour</button>
                  <div style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'var(--blue)',marginBottom:4}}>✍️ Signer {signingDoc.type==='form'?'le bon de livraison':'la facture'}</div>
                  <div style={{fontSize:12,color:'var(--slate)',marginBottom:16}}>{signingDoc.type==='form'?signingDoc.doc.formNumber:signingDoc.doc.invoiceNumber}{signingDoc.doc.entreprise&&` — ${signingDoc.doc.entreprise}`}</div>
                  {signingDoc.doc.adminSignature&&(
                    <div style={{marginBottom:16,padding:'12px 14px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Signature de l'administrateur</div>
                      <img src={signingDoc.doc.adminSignature} alt="" style={{height:52,border:'1px solid var(--border)',borderRadius:6,background:'#fff',padding:3,maxWidth:'100%',display:'block'}}/>
                      {signingDoc.doc.adminSignedAt&&<div style={{fontSize:11,color:'var(--slate-lt)',marginTop:4}}>Le {new Date(signingDoc.doc.adminSignedAt).toLocaleDateString('fr-FR')}</div>}
                    </div>
                  )}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Votre signature</div>
                    <canvas ref={el=>{sigCanvasRef.current=el;if(el)_sigInit(el)}} width={540} height={120}
                      style={{border:'1.5px solid var(--border)',borderRadius:8,touchAction:'none',display:'block',background:'#fff',cursor:'crosshair',width:'100%',maxWidth:520}}/>
                    <button style={{marginTop:8,background:'none',border:'1px solid var(--border)',borderRadius:7,padding:'5px 12px',fontSize:12,color:'var(--slate)',cursor:'pointer'}} onClick={_sigClear}>🗑 Effacer</button>
                  </div>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                    <button className="ud-save-btn" disabled={signSaving} onClick={async()=>{
                      const sig=_sigExport();if(!sig){alert('Veuillez signer avant de valider.');return}
                      setSignSaving(true)
                      try{
                        const id=signingDoc.doc._id||signingDoc.doc.id;let signed
                        if(signingDoc.type==='form'){const res=await deliveryAPI.userSignForm(id,sig);signed=res.form;setPendingForms(p=>p.map(x=>(x._id||x.id)===id?signed:x))}
                        else{const res=await deliveryAPI.userSignInvoice(id,sig);signed=res.invoice;setPendingInvoices(p=>p.map(x=>(x._id||x.id)===id?signed:x))}
                        await generateDeliveryPDF(signed,signingDoc.type)
                        setSigningDoc(null);setViewingDoc(null);setPendingDocsBadge(0)
                      }catch(e){alert('Erreur : '+(e.message||''))}finally{setSignSaving(false)}
                    }}>{signSaving?'⏳ Envoi…':'✓ Valider & télécharger le PDF signé'}</button>
                    <button className="ud-cancel-btn" disabled={signSaving} onClick={()=>setSigningDoc(null)}>Annuler</button>
                  </div>
                </div>
              ) : viewingDoc ? (
                /* ─── Vue détail — PAS de bouton PDF, seulement Signer ─── */
                <div style={{padding:'20px 18px'}}>
                  <button style={{background:'none',border:'none',color:'var(--slate)',cursor:'pointer',fontSize:13,marginBottom:14,fontFamily:"'Inter',sans-serif",padding:0}} onClick={()=>setViewingDoc(null)}>← Retour</button>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18,flexWrap:'wrap',gap:10}}>
                    <div>
                      <div style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:800,color:'var(--blue)'}}>{viewingDoc.type==='form'?viewingDoc.doc.formNumber:viewingDoc.doc.invoiceNumber}</div>
                      <div style={{fontSize:12,color:'var(--slate)',marginTop:2}}>{viewingDoc.type==='form'?'Bon de livraison':'Facture de livraison'}{viewingDoc.doc.date&&` · ${viewingDoc.doc.date}`}</div>
                    </div>
                    {viewingDoc.doc.signatureStatus==='signed'
                      ?<span style={{fontSize:11,fontWeight:700,color:'var(--green-dk)',background:'var(--green-lt)',padding:'4px 12px',borderRadius:100}}>✅ Signé par les deux parties</span>
                      :<span style={{fontSize:11,fontWeight:700,color:'#ca8a04',background:'#fef9c3',padding:'4px 12px',borderRadius:100}}>⏳ En attente de votre signature</span>
                    }
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 20px',marginBottom:16}}>
                    <div style={{background:'var(--bg)',borderRadius:8,padding:'12px 14px',border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Émetteur</div>
                      {viewingDoc.doc.entreprise&&<div style={{fontWeight:700,color:'var(--blue)',fontSize:13}}>{viewingDoc.doc.entreprise}</div>}
                      {viewingDoc.doc.tel&&<div style={{fontSize:12,color:'var(--slate)'}}>{viewingDoc.doc.tel}</div>}
                      {viewingDoc.doc.localisation&&<div style={{fontSize:12,color:'var(--slate)'}}>{viewingDoc.doc.localisation}</div>}
                    </div>
                    <div style={{background:'var(--bg)',borderRadius:8,padding:'12px 14px',border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Client</div>
                      {viewingDoc.doc.clientNom&&<div style={{fontWeight:700,color:'var(--blue)',fontSize:13}}>{viewingDoc.doc.clientNom}</div>}
                      {viewingDoc.doc.clientEmail&&<div style={{fontSize:12,color:'var(--slate)'}}>{viewingDoc.doc.clientEmail}</div>}
                      {viewingDoc.doc.clientTel&&<div style={{fontSize:12,color:'var(--slate)'}}>{viewingDoc.doc.clientTel}</div>}
                    </div>
                  </div>
                  {viewingDoc.type==='form'?(
                    <div style={{background:'var(--bg)',borderRadius:8,padding:'12px 14px',border:'1px solid var(--border)',marginBottom:16}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Détails de la livraison</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 20px'}}>
                        {[['Animal',viewingDoc.doc.animal],['Espèce',viewingDoc.doc.espece],['Départ',viewingDoc.doc.depart],['Arrivée',viewingDoc.doc.arrivee],['Prix transport',viewingDoc.doc.prixTransport?viewingDoc.doc.prixTransport+' €':null],['Date',viewingDoc.doc.date]].filter(([,v])=>v).map(([l,v])=>(
                          <div key={l}><div style={{fontSize:10,fontWeight:700,color:'var(--slate)',textTransform:'uppercase'}}>{l}</div><div style={{fontSize:13,color:'var(--blue)',fontWeight:600}}>{v}</div></div>
                        ))}
                      </div>
                    </div>
                  ):(
                    <div style={{background:'var(--bg)',borderRadius:8,padding:'12px 14px',border:'1px solid var(--border)',marginBottom:16}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Détails de la facture</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 20px'}}>
                        {[['Description',viewingDoc.doc.description],['Animal',viewingDoc.doc.animal],['Espèce',viewingDoc.doc.espece],['Date',viewingDoc.doc.date]].filter(([,v])=>v).map(([l,v])=>(
                          <div key={l}><div style={{fontSize:10,fontWeight:700,color:'var(--slate)',textTransform:'uppercase'}}>{l}</div><div style={{fontSize:13,color:'var(--blue)',fontWeight:600}}>{v}</div></div>
                        ))}
                        {viewingDoc.doc.montant&&<div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,fontWeight:700,color:'var(--slate)',textTransform:'uppercase'}}>Montant total</div><div style={{fontSize:20,fontWeight:800,color:'var(--gold-dk)'}}>{Number(viewingDoc.doc.montant).toLocaleString('fr-FR')} €</div></div>}
                      </div>
                    </div>
                  )}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px',marginBottom:20}}>
                    <div style={{background:'var(--bg)',borderRadius:8,padding:'12px 14px',border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Signature {viewingDoc.doc.entreprise||'Entreprise'}</div>
                      {viewingDoc.doc.adminSignature?<><img src={viewingDoc.doc.adminSignature} alt="" style={{height:52,border:'1px solid var(--border)',borderRadius:6,background:'#fff',padding:3,maxWidth:'100%',display:'block'}}/>{viewingDoc.doc.adminSignedAt&&<div style={{fontSize:11,color:'var(--slate-lt)',marginTop:4}}>Le {new Date(viewingDoc.doc.adminSignedAt).toLocaleDateString('fr-FR')}</div>}</>:<div style={{fontSize:12,color:'var(--slate-lt)',fontStyle:'italic'}}>En attente</div>}
                    </div>
                    <div style={{background:'var(--bg)',borderRadius:8,padding:'12px 14px',border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Votre signature</div>
                      {viewingDoc.doc.userSignature?<><img src={viewingDoc.doc.userSignature} alt="" style={{height:52,border:'1px solid var(--border)',borderRadius:6,background:'#fff',padding:3,maxWidth:'100%',display:'block'}}/>{viewingDoc.doc.userSignedAt&&<div style={{fontSize:11,color:'var(--slate-lt)',marginTop:4}}>Le {new Date(viewingDoc.doc.userSignedAt).toLocaleDateString('fr-FR')}</div>}</>:<div style={{fontSize:12,color:'var(--slate-lt)',fontStyle:'italic'}}>Non signé</div>}
                    </div>
                  </div>
                  {/* Seulement le bouton Signer — pas de Télécharger PDF ici */}
                  {viewingDoc.doc.signatureStatus==='awaiting_user'&&(
                    <button className="ud-save-btn" onClick={()=>setSigningDoc(viewingDoc)}>✍️ Signer ce document</button>
                  )}
                </div>
              ) : (
                /* ─── Liste avec filtre ─── */
                <>
                  <div style={{display:'flex',gap:8,padding:'12px 18px 0',marginBottom:0}}>
                    <button onClick={()=>setPendingFilter('forms')} style={{padding:'6px 16px',borderRadius:20,border:'none',cursor:'pointer',fontSize:12,fontWeight:700,background:pendingFilter==='forms'?'var(--blue)':'var(--bg)',color:pendingFilter==='forms'?'#fff':'var(--slate)'}}>
                      📋 Bons de livraison{pendingForms.length>0&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:100,background:pendingFilter==='forms'?'rgba(255,255,255,.25)':'var(--gold-lt)',color:pendingFilter==='forms'?'#fff':'var(--gold-dk)'}}>{pendingForms.length}</span>}
                    </button>
                    <button onClick={()=>setPendingFilter('invoices')} style={{padding:'6px 16px',borderRadius:20,border:'none',cursor:'pointer',fontSize:12,fontWeight:700,background:pendingFilter==='invoices'?'var(--blue)':'var(--bg)',color:pendingFilter==='invoices'?'#fff':'var(--slate)'}}>
                      🧾 Factures{pendingInvoices.length>0&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:100,background:pendingFilter==='invoices'?'rgba(255,255,255,.25)':'var(--gold-lt)',color:pendingFilter==='invoices'?'#fff':'var(--gold-dk)'}}>{pendingInvoices.length}</span>}
                    </button>
                  </div>
                  {pendingLoading?(<div className="ud-empty"><div className="catalog-spinner"/><p>Chargement…</p></div>)
                  :pendingFilter==='forms'?(
                    pendingForms.length===0?(<div className="ud-empty"><div className="ud-empty-icon">📋</div><h3>Aucun bon de livraison</h3><p>Les bons de livraison apparaîtront ici.</p></div>):(
                      <div className="ud-adoption-list">
                        {pendingForms.map(f=>(
                          <div key={f._id} className="ud-adoption-row" style={{cursor:'pointer',...(f.signatureStatus==='awaiting_user'&&!f.readByUser?{background:'#FFFBF0',borderColor:'var(--gold)'}:{})}}
                            onClick={()=>setViewingDoc({type:'form',doc:f})}>
                            <div className="ud-adoption-icon">📋</div>
                            <div className="ud-adoption-info">
                              <div className="ud-adoption-num">{f.formNumber||'—'}</div>
                              <div className="ud-adoption-meta">{f.entreprise||'Bon de livraison'}{f.date?` · ${f.date}`:''}</div>
                              {f.depart&&f.arrivee&&<div className="ud-adoption-sub">🚩 {f.depart} → 🏁 {f.arrivee}</div>}
                            </div>
                            {f.signatureStatus==='signed'?<span className="ud-adoption-badge">✅ Signé</span>:<span style={{fontSize:11,fontWeight:700,color:'#ca8a04',background:'#fef9c3',padding:'4px 10px',borderRadius:100,whiteSpace:'nowrap',flexShrink:0}}>⏳ À signer</span>}
                            <button className="ud-dl-btn" disabled={downloadingDeliveryId===f._id}
                              onClick={async e=>{e.stopPropagation();setDownloadingDeliveryId(f._id);try{await generateDeliveryPDF(f,'form')}catch(err){}finally{setDownloadingDeliveryId(null)}}}>
                              {downloadingDeliveryId===f._id?'…':<><IcoDownload/> PDF</>}
                            </button>
                          </div>
                        ))}
                      </div>
                    )
                  ):(
                    pendingInvoices.length===0?(<div className="ud-empty"><div className="ud-empty-icon">🧾</div><h3>Aucune facture</h3><p>Les factures de livraison apparaîtront ici.</p></div>):(
                      <div className="ud-adoption-list">
                        {pendingInvoices.map(f=>(
                          <div key={f._id} className="ud-adoption-row" style={{cursor:'pointer',...(f.signatureStatus==='awaiting_user'&&!f.readByUser?{background:'#FFFBF0',borderColor:'var(--gold)'}:{})}}
                            onClick={()=>setViewingDoc({type:'invoice',doc:f})}>
                            <div className="ud-adoption-icon">🧾</div>
                            <div className="ud-adoption-info">
                              <div className="ud-adoption-num">{f.invoiceNumber||'—'}</div>
                              <div className="ud-adoption-meta">{f.entreprise||'Facture de livraison'}{f.date?` · ${f.date}`:''}</div>
                              {f.montant&&<div className="ud-adoption-sub" style={{fontWeight:700,color:'var(--gold-dk)'}}>{Number(f.montant).toLocaleString('fr-FR')} €</div>}
                            </div>
                            {f.signatureStatus==='signed'?<span className="ud-adoption-badge">✅ Signé</span>:<span style={{fontSize:11,fontWeight:700,color:'#ca8a04',background:'#fef9c3',padding:'4px 10px',borderRadius:100,whiteSpace:'nowrap',flexShrink:0}}>⏳ À signer</span>}
                            <button className="ud-dl-btn" disabled={downloadingDeliveryId===f._id}
                              onClick={async e=>{e.stopPropagation();setDownloadingDeliveryId(f._id);try{await generateDeliveryPDF(f,'invoice')}catch(err){}finally{setDownloadingDeliveryId(null)}}}>
                              {downloadingDeliveryId===f._id?'…':<><IcoDownload/> PDF</>}
                            </button>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          )}
          {/* ══ DOCS ANIMALIERS ══ */}
          {activeView==='invoices'&&invoiceTab==='animal-docs'&&(
            <div className="ud-card">
              {animalDocsLoading ? (
                <div className="ud-empty"><div className="catalog-spinner" /><p>Chargement…</p></div>
              ) : signingAnimalDoc ? (
                /* ─── Écran signature cession — identique a-signer livraison ─── */
                <div style={{padding:'20px 18px'}}>
                  <button style={{background:'none',border:'none',color:'var(--slate)',cursor:'pointer',fontSize:13,marginBottom:14,padding:0}} onClick={()=>setSigningAnimalDoc(null)}>← Retour</button>
                  <div style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'var(--blue)',marginBottom:4}}>✍️ Signer l&apos;attestation de cession</div>
                  <div style={{fontSize:12,color:'var(--slate)',marginBottom:16}}>{signingAnimalDoc.docNumber} — {signingAnimalDoc.animalNom||'Animal'}</div>
                  {signingAnimalDoc.adminSignature&&(
                    <div style={{marginBottom:16,padding:'12px 14px',background:'var(--bg)',borderRadius:8,border:'1px solid var(--border)'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>Signature de l&apos;administrateur</div>
                      <img src={signingAnimalDoc.adminSignature} alt="" style={{height:52,border:'1px solid var(--border)',borderRadius:6,background:'#fff',padding:3,maxWidth:'100%',display:'block'}}/>
                      {signingAnimalDoc.adminSignedAt&&<div style={{fontSize:11,color:'var(--slate-lt)',marginTop:4}}>Le {new Date(signingAnimalDoc.adminSignedAt).toLocaleDateString('fr-FR')}</div>}
                    </div>
                  )}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Votre signature</div>
                    <canvas ref={el=>{sigCanvasRef.current=el;if(el)_sigInit(el)}} width={540} height={120}
                      style={{border:'1.5px solid var(--border)',borderRadius:8,touchAction:'none',display:'block',background:'#fff',cursor:'crosshair',width:'100%',maxWidth:520}}/>
                    <button style={{marginTop:8,background:'none',border:'1px solid var(--border)',borderRadius:7,padding:'5px 12px',fontSize:12,color:'var(--slate)',cursor:'pointer'}} onClick={_sigClear}>🗑 Effacer</button>
                  </div>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                    <button className="ud-save-btn" disabled={animalSignSaving} onClick={async()=>{
                      const sig=_sigExport();if(!sig){alert('Veuillez signer avant de valider.');return}
                      setAnimalSignSaving(true)
                      try{
                        const res=await animalDocsAPI.userSign(signingAnimalDoc._id,sig)
                        const signed={...signingAnimalDoc,...res.doc,userSignature:sig}
                        setAnimalDocs(p=>p.map(d=>d._id===signingAnimalDoc._id?signed:d))
                        // Télécharger PDF signé avec les 2 signatures
                        await _downloadAnimalDocPDF(signed,signed.adminSignature,sig)
                        setSigningAnimalDoc(null);setViewingAnimalDoc(null)
                      }catch(e){alert('Erreur : '+(e.message||''))}finally{setAnimalSignSaving(false)}
                    }}>{animalSignSaving?'⏳ Envoi…':'✓ Valider & télécharger le PDF signé'}</button>
                    <button className="ud-cancel-btn" disabled={animalSignSaving} onClick={()=>setSigningAnimalDoc(null)}>Annuler</button>
                  </div>
                </div>
              ) : (
                /* ─── Tableau style admin avec filtres ─── */
                <div className="adm-tbl-wrap" style={{marginBottom:0}}>
                  {/* Header avec titre + filtres */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderBottom:'1px solid var(--border)',flexWrap:'wrap',gap:10}}>
                    <div style={{fontSize:14,fontWeight:700,color:'var(--blue)',display:'flex',alignItems:'center',gap:6}}>
                      🏥 Docs santé
                      <span style={{fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:4}}>({animalDocs.length})</span>
                    </div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}} className="ud-animaldoc-filters">
                      {[
                        {id:'all',        label:'Tous'},
                        {id:'carnet',     label:'Carnet de santé'},
                        {id:'attestation',label:'Attestation vét.'},
                        {id:'identification',label:'Identification'},
                        {id:'cession',    label:'Cession'},
                      ].map(f=>(
                        <button key={f.id}
                          style={{padding:'5px 12px',borderRadius:20,border:'1.5px solid',cursor:'pointer',fontSize:11,fontWeight:700,
                            background:animalDocFilter===f.id?'var(--blue)':'var(--bg)',
                            color:animalDocFilter===f.id?'#fff':'var(--slate)',
                            borderColor:animalDocFilter===f.id?'var(--blue)':'var(--border)',
                            transition:'all .15s'}}
                          onClick={()=>{ setAnimalDocFilter(f.id); setAnimalDocPage(1) }}>
                          {f.label}
                          {animalDocs.filter(d=>f.id==='all'||d.docType===f.id).length>0&&f.id!=='all'&&(
                            <span style={{marginLeft:5,fontSize:10,padding:'1px 5px',borderRadius:100,
                              background:animalDocFilter===f.id?'rgba(255,255,255,.25)':'var(--gold-lt)',
                              color:animalDocFilter===f.id?'#fff':'var(--gold-dk)'}}>
                              {animalDocs.filter(d=>d.docType===f.id).length}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tableau */}
                  {(()=>{
                    const filtered=animalDocs.filter(d=>animalDocFilter==='all'||d.docType===animalDocFilter)
                    const totalDocPages=Math.max(1,Math.ceil(filtered.length/ANIM_DOC_PER))
                    const safePage=Math.min(animalDocPage,totalDocPages)
                    const paged=filtered.slice((safePage-1)*ANIM_DOC_PER,safePage*ANIM_DOC_PER)
                    const typeLabels={carnet:'Carnet de santé',attestation:'Attestation vétérinaire',identification:'Identification',cession:'Attestation de cession'}
                    return filtered.length===0?(
                      <div className="ud-empty" style={{padding:'40px 20px'}}>
                        <div className="ud-empty-icon">🏥</div>
                        <h3>Aucun document</h3>
                        <p>Aucun document de ce type pour l&apos;instant.</p>
                      </div>
                    ):(
                      <>
                      <div className="adm-tbl-scroll">
                        <table className="adm-tbl">
                          <thead>
                            <tr>
                              <th>Référence</th>
                              <th>Animal</th>
                              <th>Client</th>
                              <th>Date</th>
                              <th>Statut</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {paged.map(doc=>{
                              const isNew=!doc.readByUser
                              const needsSig=doc.docType==='cession'&&doc.signatureStatus==='awaiting_user'
                              return(
                                <tr key={doc._id} style={isNew?{background:'#FFFBF0'}:{}}>
                                  <td>
                                    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'nowrap'}}>
                                      <span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6,whiteSpace:'nowrap'}}>{doc.docNumber||'—'}</span>
                                      {isNew&&<span className="ud-inv-new-dot"/>}
                                    </div>
                                    <div style={{fontSize:10,color:'var(--slate)',marginTop:3}}>{typeLabels[doc.docType]||doc.docType}</div>
                                  </td>
                                  <td>
                                    <div className="adm-tbl-name">{doc.animalNom||'—'}</div>
                                    {doc.animalEspece&&<div className="adm-tbl-sub">{doc.animalEspece}</div>}
                                  </td>
                                  <td>
                                    <div className="adm-tbl-name">{doc.clientNom||'—'}</div>
                                    {doc.clientEmail&&<div className="adm-tbl-sub">{doc.clientEmail}</div>}
                                  </td>
                                  <td style={{fontSize:12,color:'var(--slate)',whiteSpace:'nowrap'}}>{doc.date||'—'}</td>
                                  <td>
                                    {needsSig
                                      ?<span className="adm-sp adm-sp-pending">⏳ À signer</span>
                                      :doc.signatureStatus==='signed'
                                        ?<span className="adm-sp adm-sp-active">✅ Signé</span>
                                        :<span className="adm-sp adm-sp-confirmed">📄 Envoyé</span>
                                    }
                                  </td>
                                  <td style={{display:'flex',gap:6,alignItems:'center',flexWrap:'nowrap'}}>
                                    <button className="adm-dl-btn" disabled={dlAnimalDocId===doc._id}
                                      onClick={async()=>{
                                        if(isNew){try{await animalDocsAPI.markRead(doc._id);setAnimalDocs(p=>p.map(d=>d._id===doc._id?{...d,readByUser:true}:d))}catch(e){}}
                                        setDlAnimalDocId(doc._id)
                                        try{
                                          const fullData = await animalDocsAPI.getMyDoc(doc._id)
                                          const fullDoc  = fullData.doc || doc
                                          await _downloadAnimalDocPDF(fullDoc, fullDoc.adminSignature||null, fullDoc.userSignature||null)
                                        }catch(err){}finally{setDlAnimalDocId(null)}
                                      }}>
                                      {dlAnimalDocId===doc._id?'…':<><IcoDownload/> PDF</>}
                                    </button>
                                    {needsSig&&(
                                      <button className="adm-btn adm-btn-primary" style={{padding:'5px 12px',fontSize:12}}
                                        onClick={async()=>{
                                          if(isNew){try{await animalDocsAPI.markRead(doc._id);setAnimalDocs(p=>p.map(d=>d._id===doc._id?{...d,readByUser:true}:d))}catch(e){}}
                                          setSigningAnimalDoc(doc)
                                        }}>✍️ Signer</button>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                      {totalDocPages>1&&(
                        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,padding:'12px 16px',borderTop:'1px solid var(--border)'}}>
                          <button style={{width:32,height:32,borderRadius:8,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:18,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',opacity:safePage===1?.35:1}} disabled={safePage===1} onClick={()=>setAnimalDocPage(p=>Math.max(1,p-1))}>‹</button>
                          <span style={{fontSize:13,fontWeight:600,color:'var(--slate)',minWidth:56,textAlign:'center'}}>{safePage} / {totalDocPages}</span>
                          <button style={{width:32,height:32,borderRadius:8,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:18,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',opacity:safePage===totalDocPages?.35:1}} disabled={safePage===totalDocPages} onClick={()=>setAnimalDocPage(p=>Math.min(totalDocPages,p+1))}>›</button>
                        </div>
                      )}
                      </>
                    )
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ══ MES TRANSPORTS ══ */}
          {activeView === 'transport' && (
            <>
              {/* ── Section Services — design identique à Services.jsx ── */}
              <div style={{ background:'var(--bg)', borderRadius:14, padding:'32px 16px 28px', marginBottom:16, border:'1px solid var(--border)' }}>
                <div style={{ maxWidth:'100%', display:'flex', flexDirection:'column', gap:40 }}>

                  {/* En-tête : texte gauche + stats/trust droite — flex responsive */}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'32px 60px', alignItems:'flex-start' }}>

                    {/* Gauche */}
                    <div style={{ flex:'1 1 280px', minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:11, fontWeight:700, color:'var(--gold)', letterSpacing:'1.4px', textTransform:'uppercase', marginBottom:18, fontFamily:"'Sora',sans-serif" }}>
                        <span style={{ display:'block', width:28, height:2, background:'var(--gold)', borderRadius:2, flexShrink:0 }} />
                        NOS SERVICES
                      </div>
                      <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:'clamp(22px,3vw,38px)', fontWeight:800, color:'var(--blue)', lineHeight:1.18, margin:'0 0 18px' }}>
                        Tout ce dont vous avez besoin<br />
                        pour <span style={{ color:'var(--gold)' }}>adopter sereinement</span>
                      </h2>
                      <p style={{ fontSize:15, color:'var(--slate)', lineHeight:1.65, maxWidth:440, margin:0 }}>
                        De la réservation du transport à la livraison finale, nous accompagnons
                        chaque étape de l'adoption de votre animal avec professionnalisme et transparence.
                      </p>
                    </div>

                    {/* Droite : stats + trust */}
                    <div style={{ flex:'1 1 260px', minWidth:0, display:'flex', flexDirection:'column', gap:24, paddingTop:6 }}>
                      <div style={{ display:'flex', gap:32 }}>
                        {[['5','Services inclus'],['24h','Délai de réponse'],['100%','Sécurisé']].map(([val,label]) => (
                          <div key={label}>
                            <div style={{ fontFamily:"'Sora',sans-serif", fontSize:30, fontWeight:800, color:'var(--blue)', lineHeight:1 }}>{val}</div>
                            <div style={{ fontSize:11, fontWeight:600, color:'var(--slate-lt)', textTransform:'uppercase', letterSpacing:'.6px', marginTop:4 }}>{label}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:10, padding:'20px', background:'var(--blue-lt)', borderRadius:12, borderLeft:'3px solid var(--gold)' }}>
                        {['Transport conforme aux réglementations','Documents vérifiés avant le départ','Suivi en temps réel à chaque étape'].map(item => (
                          <div key={item} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13.5, color:'var(--blue)', fontWeight:500 }}>
                            <span style={{ width:22, height:22, background:'var(--gold)', color:'var(--blue)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </span>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Grille 3 cartes — flex responsive */}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:18 }}>
                    {[
                      {
                        num:'01', tag:'Transport', title:'Formulaire de réservation de transport',
                        body:"Effectuez une demande de transport en quelques minutes. Renseignez l'animal, le lieu de départ, la destination et la date souhaitée. Notre équipe vous contacte pour confirmer.",
                        btnLabel:'Réserver un transport',
                        onClick: () => { onBack(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior:'smooth' }), 300) }
                      },
                      {
                        num:'02', tag:'Administratif', title:'Gestion des formalités administratives',
                        body:"Nous prenons en charge la préparation et le suivi de tous les documents requis : passeport, carnet de santé, certificat vétérinaire, documents douaniers et autorisations d'import/export.",
                        btnLabel:'En savoir plus',
                        onClick: () => onAdminFormalites('transport')
                      },
                      {
                        num:'03', tag:'Facturation', title:'Facturation sécurisée et transparente',
                        body:"Retrouvez toutes vos factures depuis votre espace client. Chaque prestation donne lieu à une facture détaillée que vous pouvez consulter, télécharger ou imprimer à tout moment.",
                        btnLabel:'Accéder à mes factures',
                        onClick: () => setActiveView('invoices')
                      },
                    ].map(svc => (
                      <div key={svc.num} style={{ flex:'1 1 240px', minWidth:0, background:'var(--white)', border:'1.5px solid var(--border)', borderRadius:16, padding:'26px 24px 22px', display:'flex', flexDirection:'column', gap:10, transition:'box-shadow .2s, border-color .2s, transform .2s' }}
                        onMouseEnter={e=>{ e.currentTarget.style.boxShadow='0 8px 32px rgba(0,0,0,.10)'; e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.transform='translateY(-3px)' }}
                        onMouseLeave={e=>{ e.currentTarget.style.boxShadow=''; e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:2 }}>
                          <span style={{ display:'flex', alignItems:'center', justifyContent:'center', width:28, height:28, borderRadius:'50%', background:'var(--gold)', color:'var(--blue)', fontSize:11, fontWeight:800, fontFamily:"'Sora',sans-serif", flexShrink:0 }}>{svc.num}</span>
                          <span style={{ fontFamily:"'Sora',sans-serif", fontSize:10.5, fontWeight:700, letterSpacing:'.8px', textTransform:'uppercase', color:'var(--slate-lt)' }}>{svc.tag}</span>
                        </div>
                        <div style={{ fontFamily:"'Sora',sans-serif", fontSize:15, fontWeight:700, color:'var(--blue)', lineHeight:1.3 }}>{svc.title}</div>
                        <p style={{ fontSize:13.5, color:'var(--slate)', lineHeight:1.65, flex:1, margin:0 }}>{svc.body}</p>
                        <button
                          onClick={svc.onClick}
                          style={{ display:'inline-flex', alignItems:'center', gap:7, marginTop:6, padding:'10px 18px', background:'var(--gold)', color:'var(--blue)', border:'none', borderRadius:100, fontSize:13, fontWeight:700, fontFamily:"'Sora',sans-serif", cursor:'pointer', alignSelf:'flex-start', transition:'background .2s, transform .15s' }}
                          onMouseEnter={e=>{ e.currentTarget.style.background='var(--gold-dk)'; e.currentTarget.style.color='var(--white)'; e.currentTarget.style.transform='translateX(2px)' }}
                          onMouseLeave={e=>{ e.currentTarget.style.background='var(--gold)'; e.currentTarget.style.color='var(--blue)'; e.currentTarget.style.transform='' }}>
                          {svc.btnLabel}
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                        </button>
                      </div>
                    ))}
                  </div>

                </div>
              </div>

              {/* ── Suivi des transports ── */}
              <TransportView
                myTrips={myTrips}
                tripsLoading={tripsLoading}
                onReload={loadMyTrips}
                API_URL={process.env.REACT_APP_API_URL || 'http://localhost:5000'}
              />
            </>
          )}

          {/* ══ MON PROFIL ══ */}
          {activeView === 'profile' && (
            <div className="ud-card">
              <div className="ud-card-head">
                <div className="ud-card-title"><IcoUser /> Informations personnelles</div>
                {!editing && <button className="ud-edit-btn" onClick={() => setEditing(true)}><IcoEdit /> Modifier</button>}
              </div>
              {saved && <div className="ud-success"><IcoCheck /> Profil enregistré avec succès</div>}
              <div className="ud-form-grid">
                <div className="ud-fg">
                  <label className="ud-label">Nom complet</label>
                  {editing ? <input className="ud-input" value={fName} onChange={e=>setFName(e.target.value)} placeholder="Jean Dupont" />
                           : <div className="ud-val">{fName || '—'}</div>}
                </div>
                <div className="ud-fg">
                  <label className="ud-label">Email</label>
                  {editing ? <input className="ud-input" type="email" value={fEmail} onChange={e=>setFEmail(e.target.value)} />
                           : <div className="ud-val">{fEmail || '—'}</div>}
                </div>
                <div className="ud-fg">
                  <label className="ud-label">Téléphone</label>
                  {editing ? <input className="ud-input" type="tel" value={fPhone} onChange={e=>setFPhone(e.target.value)} placeholder="+33 6 00 00 00 00" />
                           : <div className="ud-val">{fPhone || '—'}</div>}
                </div>
                <div className="ud-fg">
                  <label className="ud-label">Ville</label>
                  {editing ? <input className="ud-input" value={fCity} onChange={e=>setFCity(e.target.value)} placeholder="Paris" />
                           : <div className="ud-val">{fCity || '—'}</div>}
                </div>
                <div className="ud-fg ud-fg-full">
                  <label className="ud-label">Adresse</label>
                  {editing ? <input className="ud-input" value={fAddress} onChange={e=>setFAddress(e.target.value)} placeholder="12 rue des Fleurs" />
                           : <div className="ud-val">{fAddress || '—'}</div>}
                </div>
              </div>
              {editing && (
                <div className="ud-form-actions">
                  <button className="ud-save-btn" onClick={handleSave}><IcoSave /> Enregistrer</button>
                  <button className="ud-cancel-btn" onClick={() => setEditing(false)}>Annuler</button>
                </div>
              )}
            </div>
          )}

          {/* ══ PARAMÈTRES ══ */}
          {activeView === 'settings' && (
            <>
              <div className="ud-card" style={{marginBottom:14}}>
                <div className="ud-card-head"><div className="ud-card-title"><IcoBell /> Notifications</div></div>
                {[
                  {label:'Emails promotionnels', sub:'Offres et nouveautés', val:notifEmail, set:setNotifEmail},
                  {label:'Alertes messages',     sub:'Nouveaux messages reçus', val:notifMsg, set:setNotifMsg},
                  {label:'Suivi de livraison',   sub:'Mises à jour de commande', val:notifShip, set:setNotifShip},
                ].map(row => (
                  <div key={row.label} className="ud-setting-row">
                    <div>
                      <div className="ud-setting-label">{row.label}</div>
                      <div className="ud-setting-sub">{row.sub}</div>
                    </div>
                    <button className={`ud-toggle${row.val?' on':''}`} onClick={() => row.set(v=>!v)} aria-label={row.label} />
                  </div>
                ))}
              </div>
              <div className="ud-card" style={{marginBottom:14}}>
                <div className="ud-card-head"><div className="ud-card-title"><IcoLock /> Sécurité</div></div>
                <div className="ud-setting-row">
                  <div><div className="ud-setting-label">Modifier le mot de passe</div></div>
                  <button className="ud-action-btn ud-action-view-full"><IcoEdit /> Modifier</button>
                </div>
                <div className="ud-setting-row">
                  <div><div className="ud-setting-label">Authentification 2FA</div><div className="ud-setting-sub">Non activée</div></div>
                  <button className="ud-action-btn ud-action-view-full"><IcoShield /> Activer</button>
                </div>
              </div>
              <div className="ud-card">
                <div className="ud-card-head"><div className="ud-card-title" style={{color:'var(--red)'}}><IcoTrash /> Zone de danger</div></div>
                <div className="ud-setting-row">
                  <div><div className="ud-setting-label" style={{color:'var(--red)'}}>Supprimer mon compte</div><div className="ud-setting-sub">Action irréversible</div></div>
                  <button className="ud-action-btn ud-action-del-full"><IcoTrash /> Supprimer</button>
                </div>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ── Confirmation de suppression d'une annonce ── */}
      {confirmDelListing && (
        <div className="ud-confirm-overlay" onClick={() => setConfirmDelListing(null)}>
          <div className="ud-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="ud-confirm-icon"><IcoTrash /></div>
            <div className="ud-confirm-title">Supprimer cette annonce ?</div>
            <div className="ud-confirm-sub">« {confirmDelListing.title} » sera définitivement supprimée. Cette action est irréversible.</div>
            <div className="ud-confirm-actions">
              <button className="ud-cancel-btn" onClick={() => setConfirmDelListing(null)}>Annuler</button>
              <button className="ud-confirm-danger" onClick={handleDeleteListing}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}